import React, { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import liquidacionService from '../services/liquidacionService';
import planillaCerradaService from '../services/planillaCerradaService';
import gratificacionService from '../services/gratificacionService';
import ctsService from '../services/ctsService';
import { useAuth } from '../hooks/useAuth';
import { ENV_CONFIG } from '../config/environment';

// Helpers de cálculo (frontend)
function diffInMonths(d1, d2) {
  const a = parseDateSafe(d1);
  const b = parseDateSafe(d2);
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
}

function addMonths(d, months) {
  const x = parseDateSafe(d);
  x.setMonth(x.getMonth() + months);
  return x;
}

function diffInDays(d1, d2) {
  const a = parseDateSafe(d1);
  const b = parseDateSafe(d2);
  return Math.floor((b - a) / (24 * 60 * 60 * 1000));
}

// Regla de corte mensual: un mes se cumple el día anterior al mismo día de inicio.
// Ej.: 28/02 -> 27/03 = 1 mes; 28/03 ya cuenta como día del siguiente tramo.
function descomponerMesesYDiasConCorte(desde, hasta, maxMeses = Number.POSITIVE_INFINITY) {
  const dIni = parseDateSafe(desde);
  const dFin = parseDateSafe(hasta);
  if (isNaN(dIni.getTime()) || isNaN(dFin.getTime()) || dFin < dIni) {
    return { meses: 0, dias: 0, cursor: dIni };
  }
  let meses = 0;
  let cursor = new Date(dIni);
  while (meses < maxMeses) {
    const inicioSiguienteMes = new Date(cursor);
    inicioSiguienteMes.setMonth(inicioSiguienteMes.getMonth() + 1);
    const finMesComputable = new Date(inicioSiguienteMes);
    finMesComputable.setDate(finMesComputable.getDate() - 1);
    if (finMesComputable <= dFin) {
      meses++;
      cursor = inicioSiguienteMes;
    } else {
      break;
    }
  }
  const dias = Math.max(0, diffInDays(cursor, dFin) + 1);
  return { meses, dias, cursor };
}

function formatDateShort(d) {
  if (!d) return '';
  const x = parseDateSafe(d);
  return isNaN(x.getTime()) ? '' : x.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Evita desfase por zona horaria cuando la API envía fechas tipo YYYY-MM-DD.
function parseDateSafe(value) {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const soloFecha = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (soloFecha) {
      const [, y, m, d] = soloFecha;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
  }
  return new Date(value);
}

/**
 * Vacaciones truncas en liquidación.
 * Fórmula: por cada año completo = 1 período de vacaciones con (diasPorRegimen) días (según régimen: 30 General, 15 MYPE).
 * Período parcial: (meses/12 + días/360) × diasPorRegimen días.
 */
function calcularVacacionesTruncas(remun, fechaIngreso, fechaCese, diasGozados, diasPorRegimen = 30, diasIniciales = 0) {
  const dIngreso = parseDateSafe(fechaIngreso);
  const dCese = parseDateSafe(fechaCese);
  // Regla solicitada: el mes se cierra el día anterior al mismo día calendario de ingreso.
  // Ej.: ingreso 28/02 -> 1er mes completo al 27/03; 28/03 ya cuenta como día adicional.
  let mesesCompletos = 0;
  let cursorMes = new Date(dIngreso);
  while (true) {
    const inicioSiguienteMes = new Date(cursorMes);
    inicioSiguienteMes.setMonth(inicioSiguienteMes.getMonth() + 1);
    const finMesComputable = new Date(inicioSiguienteMes);
    finMesComputable.setDate(finMesComputable.getDate() - 1);
    if (finMesComputable <= dCese) {
      mesesCompletos++;
      cursorMes = inicioSiguienteMes;
    } else {
      break;
    }
  }
  const diasPorAnoSegunRegimen = diasPorRegimen;

  if (mesesCompletos < 1) {
    return {
      monto: 0,
      formula: {
        remuneracion: remun,
        meses_completos: 0,
        dias: 0,
        monto_por_meses: 0,
        monto_por_dias: 0,
        dias_gozados: diasGozados,
        dias_iniciales: diasIniciales,
        descuento_gozados: 0,
        descuento_iniciales: 0,
        por_ano: [],
        parcial: null,
        dias_por_ano_segun_regimen: diasPorAnoSegunRegimen,
        periodos_completos: 0,
        dias_proporcionales: 0,
        total_dias_que_le_pertenecen: 0,
        periodos_desglose: [],
        total_dias_pendientes: 0,
        monto_dias_pendientes: 0,
      },
    };
  }

  const anosCompletos = Math.floor(mesesCompletos / 12);
  const mesesParciales = mesesCompletos % 12;
  // Días restantes: desde el inicio del siguiente tramo (cursorMes) hasta cese, incluyendo el día actual.
  const dias = Math.max(0, diffInDays(cursorMes, fechaCese) + 1);

  // Días proporcionales del período no completo: (meses/12 + días/360) × días por año según régimen
  const diasProporcionales = Math.round((mesesParciales * (diasPorRegimen / 12) + (dias / 360) * diasPorRegimen) * 100) / 100;
  const totalDiasQueLePertenecen = anosCompletos * diasPorRegimen + diasProporcionales;

  let montoBruto = 0;
  const porAno = [];
  if (anosCompletos >= 1) {
    const montoPorAno = (remun / 30) * diasPorRegimen;
    for (let i = 0; i < anosCompletos; i++) {
      const desde = addMonths(fechaIngreso, i * 12);
      const hasta = addMonths(fechaIngreso, (i + 1) * 12);
      porAno.push({ ano: i + 1, desde, hasta, dias_ganados: diasPorRegimen, monto: montoPorAno });
      montoBruto += montoPorAno;
    }
  }

  const montoPorMeses = (remun / 12) * mesesParciales;
  const montoPorDias = (remun / 360) * dias;
  const montoParcial = montoPorMeses + montoPorDias;
  montoBruto += montoParcial;

  const descuentoGozados = (remun / 360) * diasGozados;
  const descuentoIniciales = (remun / 360) * diasIniciales;

  // Desglose por período: descontar gozados + iniciales en orden; calcular pendiente por período y total
  const periodosParaDescuento = [];
  porAno.forEach((a) => periodosParaDescuento.push({ nombre: `Año ${a.ano}`, dias: a.dias_ganados }));
  if (mesesParciales > 0 || dias > 0) {
    const diasMostrar = Math.max(0, dias);
    periodosParaDescuento.push({
      nombre: `${mesesParciales} ${mesesParciales === 1 ? 'mes' : 'meses'} ${diasMostrar} ${diasMostrar === 1 ? 'día' : 'días'}`,
      dias: diasProporcionales,
    });
  }
  let restoDescontar = diasGozados + diasIniciales;
  const periodosDesglose = periodosParaDescuento.map((p) => {
    const descontados = Math.min(p.dias, restoDescontar);
    const pendiente = Math.round((p.dias - descontados) * 100) / 100;
    restoDescontar -= descontados;
    return {
      nombre: p.nombre,
      dias_correspondientes: p.dias,
      descontados: Math.round(descontados * 100) / 100,
      pendiente,
    };
  });
  const totalDiasPendientes = Math.round(Math.max(0, totalDiasQueLePertenecen - diasGozados - diasIniciales) * 100) / 100;

  // Monto vacaciones = mismo cálculo que se muestra: (Remun/12)×(meses×D/30) + (Remun/360)×15×(días/30); años completos (Remun/30)×pendiente
  let montoDiasPendientes = 0;
  const D = diasPorRegimen;
  if (anosCompletos >= 1) {
    periodosDesglose.slice(0, anosCompletos).forEach((p) => {
      montoDiasPendientes += (remun / 30) * (p.pendiente ?? 0);
    });
  }
  if (mesesParciales > 0 || dias > 0) {
    const parteMeses = (remun / 12) * (mesesParciales * D / 30);
    const parteDias = (remun / 360) * 15 * (dias / 30);
    const descontadosParcial = periodosDesglose.length > 0 ? (periodosDesglose[periodosDesglose.length - 1].descontados ?? 0) : 0;
    montoDiasPendientes += parteMeses + parteDias - (remun / 30) * descontadosParcial;
  }
  montoDiasPendientes = Math.round(Math.max(0, montoDiasPendientes) * 100) / 100;

  const formula = {
    remuneracion: Math.round(remun * 100) / 100,
    dias_por_regimen: diasPorRegimen,
    dias_por_ano_segun_regimen: diasPorAnoSegunRegimen,
    periodos_completos: anosCompletos,
    dias_proporcionales: diasProporcionales,
    total_dias_que_le_pertenecen: Math.round(totalDiasQueLePertenecen * 100) / 100,
    meses_completos: mesesCompletos,
    anos_completos: anosCompletos,
    meses_parciales: mesesParciales,
    dias,
    por_ano: porAno.map((a) => ({ ...a, monto: Math.round(a.monto * 100) / 100 })),
    parcial: mesesParciales > 0 || dias > 0
      ? { meses: mesesParciales, dias, dias_equivalentes: diasProporcionales, monto_por_meses: Math.round(montoPorMeses * 100) / 100, monto_por_dias: Math.round(montoPorDias * 100) / 100 }
      : null,
    monto_por_meses: Math.round(montoPorMeses * 100) / 100,
    monto_por_dias: Math.round(montoPorDias * 100) / 100,
    dias_gozados: diasGozados,
    dias_iniciales: diasIniciales,
    descuento_gozados: Math.round(descuentoGozados * 100) / 100,
    descuento_iniciales: Math.round(descuentoIniciales * 100) / 100,
    periodos_desglose: periodosDesglose,
    total_dias_pendientes: totalDiasPendientes,
    monto_dias_pendientes: montoDiasPendientes,
  };

  return { monto: montoDiasPendientes, formula };
}

/**
 * CTS: fórmula legal (Remuneración + 1/6 de la gratificación) / 12 por mes.
 * Si no se pasa gratificación se usa remun (1 gratificación = 1 sueldo por semestre).
 */
function calcularCTS(remun, fechaIngreso, fechaCese, gratificacion = null) {
  const meses = diffInMonths(fechaIngreso, fechaCese);
  if (meses <= 0) return 0;
  const grat = gratificacion != null ? Number(gratificacion) : remun;
  const remunComputable = remun + grat / 6;
  const ctsMensual = remunComputable / 12;
  return Math.round(ctsMensual * Math.min(meses, 6) * 100) / 100;
}

/**
 * Períodos de CTS: mayo y noviembre cuyo mes cae entre fechaIngreso y fechaCese.
 * CTS se deposita en mayo (semestre nov-abr) y noviembre (semestre may-oct).
 */
function obtenerPeriodosCTS(fechaIngreso, fechaCese) {
  const dIngreso = parseDateSafe(fechaIngreso);
  const dCese = parseDateSafe(fechaCese);
  const anioIni = dIngreso.getFullYear();
  const anioFin = dCese.getFullYear();
  const periodos = [];
  for (let y = anioIni; y <= anioFin; y++) {
    for (const { mesPago, label } of [
      { mesPago: 4, label: `Mayo ${y}` },
      { mesPago: 10, label: `Noviembre ${y}` },
    ]) {
      const inicioMes = new Date(y, mesPago, 1);
      const finMes = new Date(y, mesPago + 1, 0);
      if (dCese < inicioMes || dIngreso > finMes) continue;
      periodos.push({ id: label, label });
    }
  }
  return periodos;
}

/**
 * Primer día del mes del último período CTS (referencia para la "cola").
 * En CTS el tiempo computable empieza el 1º del mes del depósito:
 * - Noviembre YYYY (periodo computable mayo-oct) → siguiente tramo desde 01/11/YYYY
 * - Mayo YYYY (periodo computable nov-abr) → siguiente tramo desde 01/05/YYYY
 */
function primerDiaMesUltimoPeriodoCts(periodos) {
  if (!periodos?.length) return null;
  const ultimo = periodos[periodos.length - 1];
  const matchMayo = ultimo.label.match(/^Mayo (\d{4})$/);
  const matchNov = ultimo.label.match(/^Noviembre (\d{4})$/);
  const anio = matchMayo ? parseInt(matchMayo[1], 10) : matchNov ? parseInt(matchNov[1], 10) : null;
  const mes = matchMayo ? 5 : matchNov ? 11 : null; // 1-12
  if (anio == null || mes == null) return null;
  return new Date(anio, mes - 1, 1); // 1º del mes (mes-1 en Date)
}

/**
 * CTS "cola": desde el 1º del mes del último período hasta cese.
 * Fórmula: (Remun + 1/6 Grat.)/12 × meses, máx 6.
 */
function calcularCtsCola(remun, fechaCese, ctsPeriodos, gratificacion = null) {
  const desde = primerDiaMesUltimoPeriodoCts(ctsPeriodos);
  if (!desde || !fechaCese) return 0;
  const dCese = parseDateSafe(fechaCese);
  if (dCese <= desde) return 0;
  const mesesCompletos = contarMesesCompletos(desde, dCese);
  if (mesesCompletos < 1) return 0;
  const meses = Math.min(6, mesesCompletos);
  const grat = gratificacion != null ? Number(gratificacion) : remun;
  const remunComputable = remun + grat / 6;
  const ctsMensual = remunComputable / 12;
  return Math.round(ctsMensual * meses * 100) / 100;
}

/**
 * Cuando no hay periodos mayo/noviembre: CTS desde fecha de ingreso hasta cese (incluye el día de ingreso).
 * Fórmula legal: (Remun + 1/6 Grat.)/12 × (meses + días/30), máx 6 meses.
 * Retorna { monto, totalDias, meses, dias, remunComputable } para UI.
 */
function calcularCtsIngresoACese(remun, fechaIngreso, fechaCese, gratificacion = null) {
  if (!remun || !fechaIngreso || !fechaCese) return { monto: 0, totalDias: 0, meses: 0, dias: 0, remunComputable: 0 };
  const dIngreso = parseDateSafe(fechaIngreso);
  const dCese = parseDateSafe(fechaCese);
  if (dCese < dIngreso) return { monto: 0, totalDias: 0, meses: 0, dias: 0, remunComputable: 0 };
  const totalDias = Math.floor((dCese - dIngreso) / (24 * 60 * 60 * 1000)) + 1;
  if (totalDias <= 0) return { monto: 0, totalDias: 0, meses: 0, dias: 0, remunComputable: 0 };
  const grat = gratificacion != null ? Number(gratificacion) : remun;
  const remunComputable = remun + grat / 6;
  const { meses, dias } = descomponerMesesYDiasConCorte(dIngreso, dCese, 6);
  const mesesEquivalentes = Math.min(6, meses + dias / 30);
  const ctsMensual = remunComputable / 12;
  const monto = Math.round(ctsMensual * mesesEquivalentes * 100) / 100;
  return { monto, totalDias, meses, dias, remunComputable };
}

function getRemuneracionBasica(conceptosIngresos, remunRef, conceptosIncluidos = null) {
  if (!conceptosIngresos?.length) return Number(remunRef) || 0;
  const basico = conceptosIngresos.find((c) => {
    const d = (c.descripcion || '').toUpperCase();
    return (d.includes('REMUNERACIÓN') && d.includes('BÁSICO')) || (d.includes('JORNAL') && d.includes('BÁSICO'));
  });
  const montoBasico = basico ? Number(basico.monto) || 0 : Number(remunRef) || 0;
  if (!(conceptosIncluidos instanceof Set)) return montoBasico;

  // Solo sumamos conceptos opcionales marcados por el usuario.
  const montoOpcionales = conceptosIngresos.reduce((sum, c, i) => {
    const d = (c.descripcion || '').toUpperCase();
    const esBasico = (d.includes('REMUNERACIÓN') && d.includes('BÁSICO')) || (d.includes('JORNAL') && d.includes('BÁSICO'));
    if (esBasico || !conceptosIncluidos.has(i)) return sum;
    return sum + (Number(c.monto) || 0);
  }, 0);

  return montoBasico + montoOpcionales;
}

/**
 * Cuenta cuántos meses completos de julio y cuántos de diciembre hay en el período [fechaIngreso, fechaCese].
 * Un mes está "completo" si el trabajador estaba desde el día 1 hasta el último día de ese mes.
 */
function contarJuliosYDiciembresCompletos(fechaIngreso, fechaCese) {
  const dIni = parseDateSafe(fechaIngreso);
  const dFin = parseDateSafe(fechaCese);
  const anioIni = dIni.getFullYear();
  const anioFin = dFin.getFullYear();
  let julios = 0;
  let diciembres = 0;
  for (let y = anioIni; y <= anioFin; y++) {
    const iniJulio = new Date(y, 6, 1);
    const finJulio = new Date(y, 6, 31);
    if (dIni <= finJulio && dFin >= iniJulio && dIni <= iniJulio && dFin >= finJulio) julios++;
    const iniDic = new Date(y, 11, 1);
    const finDic = new Date(y, 11, 31);
    if (dIni <= finDic && dFin >= iniDic && dIni <= iniDic && dFin >= finDic) diciembres++;
  }
  return { juliosCompletos: julios, diciembresCompletos: diciembres };
}

/**
 * Lista de periodos de gratificación: solo los julio y diciembre cuyo MES cae entre ingreso y cese.
 * Es decir, cuántos julios y cuántos diciembres han pasado en el período laborado.
 * Cada periodo es "Julio AÑO" (semestre ene-jun, pago en julio) o "Diciembre AÑO" (semestre jul-dic, pago en diciembre).
 */
function obtenerPeriodosGratificacion(remun, fechaIngreso, fechaCese, regimenCodigo, regimenNombre) {
  const dIngreso = parseDateSafe(fechaIngreso);
  const dCese = parseDateSafe(fechaCese);
  const codigo = (regimenCodigo || '').toUpperCase();
  const nombre = (regimenNombre || '').toLowerCase();
  const esMype = ['MYPE', 'MYPP'].includes(codigo) || nombre.includes('mype') || nombre.includes('micro') || nombre.includes('pequeña') || nombre.includes('pequena');
  const anioIni = dIngreso.getFullYear();
  const anioFin = dCese.getFullYear();
  const periodos = [];
  for (let y = anioIni; y <= anioFin; y++) {
    for (const sem of [
      { mes: 0, finMes: 5, mesPago: 6, label: `Julio ${y}` },
      { mes: 6, finMes: 11, mesPago: 11, label: `Diciembre ${y}` },
    ]) {
      const inicioMesPago = new Date(y, sem.mesPago, 1);
      const finMesPago = new Date(y, sem.mesPago, 31);
      // Solo si ese julio o diciembre ha pasado entre ingreso y cese (el mes de pago cae dentro del período)
      if (dCese < inicioMesPago || dIngreso > finMesPago) continue;
      const inicioSem = new Date(y, sem.mes, 1);
      const finSem = new Date(y, sem.finMes, 31);
      const desde = dIngreso > inicioSem ? dIngreso : inicioSem;
      const hasta = dCese < finSem ? dCese : finSem;
      const diasTrabajados = Math.floor((hasta - desde) / (24 * 60 * 60 * 1000)) + 1;
      const diasSemestre = Math.floor((finSem - inicioSem) / (24 * 60 * 60 * 1000)) + 1;
      const mesesTrabajados = Math.min(6, Math.max(0, (6 * diasTrabajados) / diasSemestre));
      let monto = (remun / 6) * mesesTrabajados;
      if (esMype) monto *= 0.5;
      periodos.push({ id: `${sem.label}`, label: sem.label, monto: Math.round(monto * 100) / 100 });
    }
  }
  return periodos;
}

function calcularGratificacionProporcional(remun, fechaIngreso, fechaCese, regimenCodigo, regimenNombre) {
  const periodos = obtenerPeriodosGratificacion(remun, fechaIngreso, fechaCese, regimenCodigo, regimenNombre);
  return periodos.reduce((sum, p) => sum + p.monto, 0);
}

/**
 * Fin del período cerrado para la gratificación del último período listado.
 * La gratificación de Julio corresponde a ene–jun, y la de Diciembre a jul–dic.
 * Por tanto: "Julio AÑO" → último día del semestre ene–jun = 30/06/AÑO (cola desde 01/07).
 * "Diciembre AÑO" → último día del semestre jul–dic = 31/12/AÑO (cola desde 01/01 del año siguiente).
 */
function ultimoDiaMesUltimoPeriodo(periodos) {
  if (!periodos?.length) return null;
  const ultimo = periodos[periodos.length - 1];
  const matchJulio = ultimo.label.match(/^Julio (\d{4})$/);
  const matchDic = ultimo.label.match(/^Diciembre (\d{4})$/);
  const anio = matchJulio ? parseInt(matchJulio[1], 10) : matchDic ? parseInt(matchDic[1], 10) : null;
  if (anio == null) return null;
  // Julio = gratificación ene-jun → período cerrado termina 30 junio → día siguiente = 01/07
  // Diciembre = gratificación jul-dic → período cerrado termina 31 diciembre → día siguiente = 01/01
  const ultimoDia = matchJulio
    ? new Date(anio, 6, 0)   // mes 6 = julio en JS, día 0 = último día de junio (30/06)
    : matchDic
      ? new Date(anio, 12, 0) // día 0 del mes 13 = 31/12
      : null;
  return ultimoDia && !isNaN(ultimoDia.getTime()) ? ultimoDia : null;
}

/**
 * Cuenta cuántos meses calendario completos hay entre inicio y fecha cese (mes cerrado = todo el mes dentro del rango).
 */
function contarMesesCompletos(desde, hasta) {
  const d = new Date(desde.getFullYear(), desde.getMonth(), 1);
  const dCese = parseDateSafe(hasta);
  let count = 0;
  while (d <= dCese) {
    const ultimoDiaDelMes = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    if (ultimoDiaDelMes <= dCese) count++;
    d.setMonth(d.getMonth() + 1);
  }
  return count;
}

/**
 * Gratificación proporcional de la "cola": desde el día siguiente al fin del semestre del último período
 * (Julio → 01/07, Diciembre → 01/01) hasta fecha de cese.
 * Regla: si no hay al menos 1 mes calendario completo = 0.
 * Si sí hay, se computa (meses + días/30).
 * Base: (Remun/6) × (meses + días/30). Bonificación 9%: (Remun × 9% / 6) × (meses + días/30). Total = base + bonificación 9%.
 */
function calcularGratificacionCola(remun, fechaCese, periodos, regimenCodigo, regimenNombre) {
  const ref = ultimoDiaMesUltimoPeriodo(periodos);
  if (!ref || !fechaCese) return { montoBase: 0, bonificacion9: 0, monto: 0, meses: 0, dias: 0, totalDias: 0 };
  const dCese = parseDateSafe(fechaCese);
  const diaSiguiente = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() + 1);
  if (dCese < diaSiguiente) return { montoBase: 0, bonificacion9: 0, monto: 0, meses: 0, dias: 0, totalDias: 0 };
  const mesesCompletos = contarMesesCompletos(diaSiguiente, dCese);
  if (mesesCompletos < 1) return { montoBase: 0, bonificacion9: 0, monto: 0, meses: 0, dias: 0, totalDias: 0 };
  const mesesTope = Math.min(6, mesesCompletos);
  const { meses, dias } = descomponerMesesYDiasConCorte(diaSiguiente, dCese, mesesTope);
  const factor = Math.min(6, meses + dias / 30);
  const codigo = (regimenCodigo || '').toUpperCase();
  const nombre = (regimenNombre || '').toLowerCase();
  const esMype = ['MYPE', 'MYPP'].includes(codigo) || nombre.includes('mype') || nombre.includes('micro') || nombre.includes('pequeña') || nombre.includes('pequena');
  let montoBase = (remun / 6) * factor;
  if (esMype) montoBase *= 0.5;
  montoBase = Math.round(montoBase * 100) / 100;
  let bonificacion9 = (remun * 0.09 / 6) * factor;
  if (esMype) bonificacion9 *= 0.5;
  bonificacion9 = Math.round(bonificacion9 * 100) / 100;
  const monto = Math.round((montoBase + bonificacion9) * 100) / 100;
  return { montoBase, bonificacion9, monto, meses, dias, totalDias: 0 };
}

/**
 * Cuando no hay periodos julio/diciembre en el rango: gratificación desde ingreso hasta cese
 * por total de meses y días (ingreso → cese). Incluye bonificación extraordinaria 9%.
 * Base: (Remun/6) × (meses + días/30), MYPE 50%.
 * Bonificación 9%: (Remun × 9% / 6) × (meses + días/30).
 * Total = base + bonificación 9%.
 */
function calcularGratificacionIngresoACese(remun, fechaIngreso, fechaCese, regimenCodigo, regimenNombre) {
  if (!remun || !fechaIngreso || !fechaCese) return { monto: 0, montoBase: 0, bonificacion9: 0, totalDias: 0, meses: 0, dias: 0 };
  const dIngreso = parseDateSafe(fechaIngreso);
  const dCese = parseDateSafe(fechaCese);
  if (dCese < dIngreso) return { monto: 0, montoBase: 0, bonificacion9: 0, totalDias: 0, meses: 0, dias: 0 };

  // Solo computar meses calendario completos en el tramo ingreso -> cese.
  // Si no hay al menos 1 mes completo, no corresponde gratificación.
  let mesesCompletosCalendario = 0;
  const cursor = new Date(dIngreso.getFullYear(), dIngreso.getMonth(), 1);
  while (cursor <= dCese) {
    const inicioMes = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const finMes = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    if (dIngreso <= inicioMes && dCese >= finMes) {
      mesesCompletosCalendario++;
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }

  if (mesesCompletosCalendario < 1) {
    return { monto: 0, montoBase: 0, bonificacion9: 0, totalDias: 0, meses: 0, dias: 0 };
  }

  const totalDias = Math.floor((dCese - dIngreso) / (24 * 60 * 60 * 1000)) + 1;
  const codigo = (regimenCodigo || '').toUpperCase();
  const nombre = (regimenNombre || '').toLowerCase();
  const esMype = ['MYPE', 'MYPP'].includes(codigo) || nombre.includes('mype') || nombre.includes('micro') || nombre.includes('pequeña') || nombre.includes('pequena');
  const mesesTope = Math.min(6, mesesCompletosCalendario);
  const { meses, dias } = descomponerMesesYDiasConCorte(dIngreso, dCese, mesesTope);
  const mesesEquivalentes = Math.min(6, meses + dias / 30);
  let montoBase = (remun / 6) * mesesEquivalentes;
  if (esMype) montoBase *= 0.5;
  montoBase = Math.round(montoBase * 100) / 100;
  // Bonificación 9% con mismo factor (meses + días/30), tope 6.
  const factorDias = Math.min(6, meses + dias / 30);
  let bonificacion9 = (remun * 0.09 / 6) * factorDias;
  if (esMype) bonificacion9 *= 0.5;
  bonificacion9 = Math.round(bonificacion9 * 100) / 100;
  const monto = Math.round((montoBase + bonificacion9) * 100) / 100;
  return { monto, montoBase, bonificacion9, totalDias, meses, dias };
}

/**
 * Construye el objeto detalle_calculos con todos los pasos y fórmulas para guardar con la liquidación.
 */
function buildDetalleCalculos(opts) {
  const {
    calc,
    detalle,
    esRegimenMype,
    esRegimenMicroEmpresa,
    vacacionesMonto,
    gratMonto,
    ctsMonto,
    bonifGraciosa,
    totalLiquidacion,
    formatCurrency,
    gratificacionSinPeriodosResult,
    ctsSinPeriodosResult,
    gratificacionCola,
    ctsCola,
    gratificacionEstados,
    ctsEstados,
    gratificacionMontoPorPeriodo,
    ctsMontoPorPeriodo,
    gratificacionParaCtsValor,
    montoGratificacionPeriodo,
    montoCtsPeriodo,
    ultimoDiaMesUltimoPeriodo,
    primerDiaMesUltimoPeriodoCts,
    contarMesesCompletos,
    formatDateShort,
  } = opts;

  const pasosVacaciones = [];
  if (calc.vacaciones_formula) {
    const vf = calc.vacaciones_formula;
    const D = vf.dias_por_ano_segun_regimen ?? 15;
    const remun = vf.remuneracion ?? 0;
    const monto = vf.monto_dias_pendientes ?? 0;
    if (vf.periodos_desglose?.length) {
      vf.periodos_desglose.forEach((p) => {
        pasosVacaciones.push({
          descripcion: `Período: ${p.nombre}`,
          formula: `Días correspondientes: ${p.dias_correspondientes}, descontados: ${p.descontados}, pendiente: ${p.pendiente} días`,
        });
      });
    }
    // Fórmula en dos partes: (Remun/12) da un resultado; (Remun/360)×15 da otro; total = suma
    const tieneParcial = vf.parcial && (vf.parcial.meses > 0 || vf.parcial.dias > 0);
    const anos = vf.periodos_completos ?? 0;
    const periodosCompletosPendientes = (vf.periodos_desglose || []).filter(
      (p) => /^Año\s+\d+/i.test(String(p.nombre || '')) && Number(p.pendiente || 0) > 0
    );
    const montoPeriodosCompletos = periodosCompletosPendientes.reduce(
      (sum, p) => sum + ((remun / 30) * Number(p.pendiente || 0)),
      0
    );
    if (tieneParcial) {
      const { meses, dias } = vf.parcial;
      // Parte meses: (Remun/12) × (meses × D/30) = primer resultado
      const parteMeses = Math.round((remun / 12) * (meses * D / 30) * 100) / 100;
      // Parte días: (Remun/360) × 15 × (días/30) = segundo resultado
      const parteDias = Math.round((remun / 360) * 15 * (dias / 30) * 100) / 100;
      periodosCompletosPendientes.forEach((p) => {
        const montoPeriodo = Math.round((remun / 30) * Number(p.pendiente || 0) * 100) / 100;
        pasosVacaciones.push({
          descripcion: `Resultado período ${p.nombre}`,
          formula: `(Remun/30) × ${p.pendiente} días = (${formatCurrency(remun)}/30) × ${p.pendiente} = ${formatCurrency(montoPeriodo)}`,
          valor: montoPeriodo,
        });
      });
      pasosVacaciones.push({
        descripcion: 'Primer resultado (por meses)',
        formula: `(Remun/12) × (meses × ${D}/30) = (${formatCurrency(remun)}/12) × (${meses} × ${D}/30) = ${formatCurrency(parteMeses)}`,
        valor: parteMeses,
      });
      pasosVacaciones.push({
        descripcion: 'Segundo resultado (por días)',
        formula: `(Remun/360) × 15 × (días/30) = (${formatCurrency(remun)}/360) × 15 × (${dias}/30) = ${formatCurrency(parteDias)}`,
        valor: parteDias,
      });
      pasosVacaciones.push({
        descripcion: 'Monto vacaciones truncas',
        formula: `Total: ${formatCurrency(montoPeriodosCompletos)} + ${formatCurrency(parteMeses)} + ${formatCurrency(parteDias)} = ${formatCurrency(monto)}`,
        valor: monto,
      });
    } else if (anos > 0) {
      const formulaAnos = `(Remun/30) × (años × ${D}) = (${formatCurrency(remun)}/30) × (${anos} × ${D}) = ${formatCurrency(monto)}`;
      pasosVacaciones.push({
        descripcion: 'Monto vacaciones truncas',
        formula: formulaAnos,
        valor: monto,
      });
    } else {
      pasosVacaciones.push({
        descripcion: 'Monto vacaciones truncas',
        formula: `${formatCurrency(monto)}`,
        valor: monto,
      });
    }
  }

  const pasosGratificacion = [];
  let tipoGratificacion = 'ninguno';
  if (esRegimenMicroEmpresa) {
    pasosGratificacion.push({
      descripcion: 'Régimen micro empresa',
      formula: 'No aplica gratificación trunca (régimen micro empresa).',
      valor: 0,
    });
  } else if (calc.gratificacion_periodos?.length > 0) {
      tipoGratificacion = 'periodos';
      calc.gratificacion_periodos.forEach((p) => {
        const estado = gratificacionEstados[p.id] ?? 'pagado';
        const monto = estado === 'pendiente' ? (montoGratificacionPeriodo ? montoGratificacionPeriodo(p) : p.monto) : 0;
        pasosGratificacion.push({
          descripcion: `Período ${p.label}`,
          formula: `Estado: ${estado}${estado === 'pendiente' ? `, monto: ${formatCurrency(monto)}` : ''}`,
          valor: estado === 'pendiente' ? monto : undefined,
        });
      });
      if (gratificacionCola && gratificacionCola.monto > 0 && detalle) {
        const remun = calc.remuneracion_basica ?? 0;
        const { montoBase, bonificacion9, monto, meses, dias } = gratificacionCola;
        pasosGratificacion.push({
          descripcion: 'Base gratificación cola (desde último período hasta cese, con meses + días)',
          formula: `(Remun/6) × (meses + días/30) ${esRegimenMype ? '× 0,5 (MYPE)' : ''} = (${formatCurrency(remun)}/6) × (${meses} + ${dias}/30)${esRegimenMype ? ' × 0,5' : ''} = ${formatCurrency(montoBase)}`,
          valor: montoBase,
        });
        pasosGratificacion.push({
          descripcion: 'Bonificación extraord. 9% (cola)',
          formula: `(Remun × 9% / 6) × (meses + días/30) ${esRegimenMype ? '× 0,5 (MYPE)' : ''} = (${formatCurrency(remun)} × 9% / 6) × (${meses} + ${dias}/30)${esRegimenMype ? ' × 0,5' : ''} = ${formatCurrency(bonificacion9)}`,
          valor: bonificacion9,
        });
        pasosGratificacion.push({
          descripcion: 'Total gratificación trunca (cola)',
          formula: `Base + Bonif. 9% = ${formatCurrency(montoBase)} + ${formatCurrency(bonificacion9)} = ${formatCurrency(monto)}`,
          valor: monto,
        });
      }
    } else if (gratificacionSinPeriodosResult) {
      tipoGratificacion = 'ingreso_cese';
      const { meses, dias, montoBase, bonificacion9 } = gratificacionSinPeriodosResult;
      const remun = calc.remuneracion_basica ?? 0;
      const mypeSufix = esRegimenMype ? ' × 0,5 (MYPE)' : '';
      pasosGratificacion.push({
        descripcion: 'Base gratificación (ingreso hasta cese)',
        formula: `(Remun/6) × (meses + días/30)${mypeSufix} = (${formatCurrency(remun)}/6) × (${meses} + ${dias}/30)${esRegimenMype ? ' × 0,5' : ''} = ${formatCurrency(montoBase)}`,
        valor: montoBase,
      });
      pasosGratificacion.push({
        descripcion: 'Bonificación extraord. 9%',
        formula: `(Remun × 9% / 6) × (meses + días/30)${mypeSufix} = (${formatCurrency(remun)} × 9% / 6) × (${meses} + ${dias}/30)${esRegimenMype ? ' × 0,5' : ''} = ${formatCurrency(bonificacion9)}`,
        valor: bonificacion9,
      });
      pasosGratificacion.push({
        descripcion: 'Total gratificación trunca',
        formula: `Base + Bonif. 9% = ${formatCurrency(montoBase)} + ${formatCurrency(bonificacion9)} = ${formatCurrency(gratificacionSinPeriodosResult.monto)}`,
        valor: gratificacionSinPeriodosResult.monto,
      });
    }

  const pasosCts = [];
  let tipoCts = 'ninguno';
  if (esRegimenMicroEmpresa) {
    pasosCts.push({
      descripcion: 'Régimen micro empresa',
      formula: 'No aplica CTS (régimen micro empresa).',
      valor: 0,
    });
  } else {
    const remun = calc.remuneracion_basica ?? 0;
    const grat = gratificacionParaCtsValor ?? remun;
    const remunComputable = remun + grat / 6;
    if (calc.cts_periodos?.length > 0) {
      tipoCts = 'periodos';
      calc.cts_periodos.forEach((p) => {
        const estado = ctsEstados[p.id] ?? 'pagado';
        const monto = estado === 'pendiente' && montoCtsPeriodo ? montoCtsPeriodo(p) : 0;
        pasosCts.push({
          descripcion: `Período ${p.label}`,
          formula: `Estado: ${estado}${estado === 'pendiente' ? `, monto: ${formatCurrency(monto)}` : ''}`,
          valor: estado === 'pendiente' ? monto : undefined,
        });
      });
      if (ctsCola > 0 && detalle) {
        const desde = primerDiaMesUltimoPeriodoCts(calc.cts_periodos);
        const dCese = parseDateSafe(detalle.fecha_cese);
        const mesesCola = desde ? contarMesesCompletos(desde, dCese) : 0;
        const mesesFormula = Math.min(6, mesesCola);
        const ctsMensual = remunComputable / 12;
        const ctsColaMonto = esRegimenMype ? Math.round(ctsCola * 0.5 * 100) / 100 : ctsCola;
        pasosCts.push({
          descripcion: 'CTS cola (desde 1º del mes último período hasta cese)',
          formula: `Remun. computable = Remun + Grat./6 = ${formatCurrency(remun)} + ${formatCurrency(grat)}/6 = ${formatCurrency(remunComputable)}. CTS mensual × meses ${esRegimenMype ? '× 0,5 (MYPE)' : ''} = ${formatCurrency(ctsMensual)} × ${mesesFormula} ${esRegimenMype ? '× 0,5' : ''} = ${formatCurrency(ctsColaMonto)}`,
          valor: ctsColaMonto,
        });
      }
    } else if (ctsSinPeriodosResult) {
      tipoCts = 'ingreso_cese';
      const { meses, dias, remunComputable: rc } = ctsSinPeriodosResult;
      const ctsMensual = rc / 12;
      pasosCts.push({
        descripcion: 'Remun. computable para CTS',
        formula: `Remun + Grat./6 = ${formatCurrency(remun)} + ${formatCurrency(grat)}/6 = ${formatCurrency(rc)}`,
        valor: rc,
      });
      pasosCts.push({
        descripcion: 'CTS desde ingreso hasta cese',
        formula: `CTS mensual × (meses + días/30) ${esRegimenMype ? '× 0,5 (MYPE)' : ''} = ${formatCurrency(ctsMensual)} × (${meses} + ${dias}/30) ${esRegimenMype ? '× 0,5' : ''} = ${formatCurrency(ctsMonto)}`,
        valor: ctsMonto,
      });
    }
  }

  const desgloseTotal = esRegimenMicroEmpresa
    ? `Vacaciones truncas (${formatCurrency(vacacionesMonto)}) + Bonificación graciosa (${formatCurrency(bonifGraciosa)}) = ${formatCurrency(totalLiquidacion)}`
    : `Vacaciones truncas (${formatCurrency(vacacionesMonto)}) + Gratificación trunca (${formatCurrency(gratMonto)}) + CTS (${formatCurrency(ctsMonto)}) + Bonificación graciosa (${formatCurrency(bonifGraciosa)}) = ${formatCurrency(totalLiquidacion)}`;

  return {
    vacaciones_truncas: {
      monto: vacacionesMonto,
      pasos: pasosVacaciones,
    },
    gratificacion_trunca: {
      monto: gratMonto,
      tipo: tipoGratificacion,
      pasos: pasosGratificacion,
    },
    cts: {
      monto: ctsMonto,
      tipo: tipoCts,
      pasos: pasosCts,
    },
    bonificacion_graciosa: {
      monto: bonifGraciosa,
      formula: 'Monto ingresado manualmente',
    },
    total: {
      monto: totalLiquidacion,
      formula: desgloseTotal,
      desglose: [vacacionesMonto, gratMonto, ctsMonto, bonifGraciosa],
    },
  };
}

const LiquidacionesDetalle = ({ empresaId, onVolver, onLiquidacionesChange }) => {
  const { selectedCompany } = useAuth();
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalNuevaLiquidacion, setModalNuevaLiquidacion] = useState(false);
  const [firmaEmpresaBase64, setFirmaEmpresaBase64] = useState(null);
  const [generandoPdfLiquidacion, setGenerandoPdfLiquidacion] = useState(false);
  const [empleadosCesados, setEmpleadosCesados] = useState([]);
  const [calculoCompleto, setCalculoCompleto] = useState([]);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [paginaActual, setPaginaActual] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginacion, setPaginacion] = useState({ total: 0, last_page: 1, current_page: 1 });
  const skipAutoSearchRef = useRef(false);
  const [conceptosIncluidos, setConceptosIncluidos] = useState(new Set());
  const [vacacionesTruncasExpandido, setVacacionesTruncasExpandido] = useState(false);
  const [montoVacacionesTruncasEditable, setMontoVacacionesTruncasEditable] = useState(null);
  const [gratificacionExpandido, setGratificacionExpandido] = useState(false);
  const [montoGratificacionEditable, setMontoGratificacionEditable] = useState(null);
  const [ctsExpandido, setCtsExpandido] = useState(false);
  const [montoCtsEditable, setMontoCtsEditable] = useState(null);
  const [gratificacionEstados, setGratificacionEstados] = useState({});
  const [gratificacionMontoPorPeriodo, setGratificacionMontoPorPeriodo] = useState({});
  const [gratificacionMontoMostrarInput, setGratificacionMontoMostrarInput] = useState({});
  const [gratificacionMontoCargando, setGratificacionMontoCargando] = useState({});
  const [ctsEstados, setCtsEstados] = useState({});
  const [ctsMontoPorPeriodo, setCtsMontoPorPeriodo] = useState({});
  const [ctsMontoMostrarInput, setCtsMontoMostrarInput] = useState({});
  const [ctsMontoCargando, setCtsMontoCargando] = useState({});
  const [gratificacionParaCts, setGratificacionParaCts] = useState(null);
  const [gratificacionParaCtsMostrarInput, setGratificacionParaCtsMostrarInput] = useState(false);
  const [gratificacionParaCtsCargando, setGratificacionParaCtsCargando] = useState(false);
  const [gratificacionParaCtsEditable, setGratificacionParaCtsEditable] = useState(null);
  const [bonificacionGraciosa, setBonificacionGraciosa] = useState(0);
  const [incluirEnPlanilla, setIncluirEnPlanilla] = useState(false);
  const [liquidacionABorrar, setLiquidacionABorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const [liquidacionAVer, setLiquidacionAVer] = useState(null);

  const formatCurrency = (amount) => {
    const n = Number(amount) || 0;
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 2,
    }).format(n);
  };

  const formatDateToDDMMYYYY = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = parseDateSafe(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const loadLiquidaciones = () => {
    if (!empresaId) return;
    setLoading(true);
    liquidacionService
      .list(empresaId)
      .then((res) => setLiquidaciones(res.data || []))
      .catch((err) => setError(err.response?.data?.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  const handleBorrarLiquidacion = (liquidacion) => {
    setLiquidacionABorrar(liquidacion);
  };

  const handleConfirmarBorrar = () => {
    if (!liquidacionABorrar) return;
    setError(null);
    setBorrando(true);
    liquidacionService
      .destroy(liquidacionABorrar.id)
      .then(() => {
        setLiquidacionABorrar(null);
        loadLiquidaciones();
        onLiquidacionesChange?.();
      })
      .catch((err) => setError(err.response?.data?.message || 'Error al eliminar'))
      .finally(() => setBorrando(false));
  };

  const handleCerrarModalBorrar = () => {
    if (!borrando) setLiquidacionABorrar(null);
  };

  const handleVerLiquidacion = (liquidacion) => {
    setLiquidacionAVer(liquidacion);
  };

  // Si el monto final de la sección no coincide con el último valor calculado, ocultamos fórmulas.
  const esDetalleConsistente = (seccion) => {
    if (!seccion) return true;
    const pasos = Array.isArray(seccion.pasos) ? seccion.pasos : [];
    if (pasos.length === 0) return true;
    const valores = pasos
      .map((p) => Number(p?.valor))
      .filter((v) => Number.isFinite(v));
    if (valores.length === 0) return true;
    const ultimoValorCalculado = valores[valores.length - 1];
    const montoSeccion = Number(seccion.monto || 0);
    return Math.abs(montoSeccion - ultimoValorCalculado) < 0.01;
  };

  // Cargar firma de la empresa para PDF (mismo patrón que PlanillaDetalle)
  useEffect(() => {
    if (!empresaId) return;
    const token = localStorage.getItem('token') || localStorage.getItem('planillas_sanctum_token') || localStorage.getItem(ENV_CONFIG.SANCTUM_TOKEN_KEY);
    fetch(`${ENV_CONFIG.API_BASE_URL}/get-firma-base64/${empresaId}`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.success && data?.firma_base64) setFirmaEmpresaBase64(data.firma_base64);
        else setFirmaEmpresaBase64(null);
      })
      .catch(() => setFirmaEmpresaBase64(null));
  }, [empresaId]);

  const exportarLiquidacionPdf = async (liquidacion) => {
    if (!liquidacion) return;
    setGenerandoPdfLiquidacion(true);
    try {
      const formatCurrencyPdf = (amount) => {
        const n = Number(amount) || 0;
        return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 2 }).format(n);
      };
      const formatDatePdf = (dateStr) => {
        if (!dateStr) return '—';
        const d = parseDateSafe(dateStr);
        return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
      };
      const nombreEmpresa = selectedCompany?.name || selectedCompany?.nombre || 'EMPRESA';
      const rucEmpresa = selectedCompany?.ruc || 'N/A';
      const detalle = liquidacion.detalle_calculos || {};
      // Usar montos del detalle guardado cuando existan, para que el reporte coincida con lo guardado (incl. cola + 9%)
      const vacaciones = Number(detalle.vacaciones_truncas?.monto ?? liquidacion.vacaciones_truncas) || 0;
      const gratificacion = Number(detalle.gratificacion_trunca?.monto ?? liquidacion.gratificacion_proporcional) || 0;
      const cts = Number(detalle.cts?.monto ?? liquidacion.cts) || 0;
      const bonificacion = Number(detalle.bonificacion_graciosa?.monto ?? 0) || 0;
      const total = Number(liquidacion.total_liquidacion) || 0;

      const renderPasos = (pasos) => {
        if (!Array.isArray(pasos) || pasos.length === 0) return '';
        return pasos.map((p) => `
          <div style="margin: 4px 0 4px 10px; padding-left: 8px; border-left: 3px solid #9ca3af; font-size: 10px; color: #374151; line-height: 1.35;">
            <strong style="color: #1f2937;">${(p.descripcion || '').replace(/</g, '&lt;')}</strong>
            ${p.formula ? `<div style="margin-top: 2px; color: #4b5563;">${(p.formula || '').replace(/</g, '&lt;')}</div>` : ''}
          </div>
        `).join('');
      };

      const bloqueConcepto = (titulo, monto, contenidoDetalle, esTotal = false) => {
        const estilo = esTotal ? 'background: #dbeafe; border: 1px solid #3b82f6;' : 'background: #f9fafb; border: 1px solid #e5e7eb;';
        const estiloTitulo = esTotal ? 'color: #1e40af;' : 'color: #1f2937;';
        return `
          <div style="margin-bottom: 12px; padding: 10px; border-radius: 6px; ${estilo}">
            <h4 style="margin: 0 0 6px 0; font-size: 12px; font-weight: bold; ${estiloTitulo}">${titulo} — ${formatCurrencyPdf(monto)}</h4>
            ${contenidoDetalle ? `<div style="margin-top: 6px;">${contenidoDetalle}</div>` : ''}
          </div>`;
      };

      const detalleVacaciones = esDetalleConsistente(detalle.vacaciones_truncas)
        ? (detalle.vacaciones_truncas?.pasos?.length ? renderPasos(detalle.vacaciones_truncas.pasos) : '')
        : '';
      const pasosGratificacionPdf = (detalle.gratificacion_trunca?.pasos || []).filter(
        (p) => !(p.formula || '').includes('Estado: pagado')
      );
      const detalleGratificacion = esDetalleConsistente(detalle.gratificacion_trunca)
        ? (pasosGratificacionPdf.length ? renderPasos(pasosGratificacionPdf) : '')
        : '';
      const pasosCtsPdf = (detalle.cts?.pasos || []).filter(
        (p) => !(p.formula || '').includes('Estado: pagado')
      );
      const detalleCts = esDetalleConsistente(detalle.cts)
        ? (pasosCtsPdf.length ? renderPasos(pasosCtsPdf) : '')
        : '';
      const detalleBonificacion = detalle.bonificacion_graciosa?.formula
        ? `<p style="margin: 0; font-size: 10px; color: #4b5563;">${(detalle.bonificacion_graciosa.formula || '').replace(/</g, '&lt;')}</p>`
        : '';
      const detalleTotal = detalle.total?.formula
        ? `<p style="margin: 0; font-size: 10px; color: #1e40af;">${(detalle.total.formula || '').replace(/</g, '&lt;')}</p>`
        : '';

      const mostrarGratificacionTrunca = gratificacion > 0;
      const bloqueConceptos = `
        <div style="margin-bottom: 20px;">
          ${bloqueConcepto('Vacaciones truncas', vacaciones, detalleVacaciones)}
          ${mostrarGratificacionTrunca ? bloqueConcepto('Gratificación trunca', gratificacion, detalleGratificacion) : ''}
          ${bloqueConcepto('CTS', cts, detalleCts)}
          ${bloqueConcepto('Bonificación graciosa', bonificacion, detalleBonificacion)}
          ${bloqueConcepto('TOTAL LIQUIDACIÓN', total, detalleTotal, true)}
        </div>`;

      const contenedorTemporal = document.createElement('div');
      contenedorTemporal.style.position = 'absolute';
      contenedorTemporal.style.left = '-9999px';
      contenedorTemporal.style.top = '0';
      contenedorTemporal.style.width = '900px';
      contenedorTemporal.style.backgroundColor = 'white';
      contenedorTemporal.style.padding = '20px';
      contenedorTemporal.style.fontFamily = 'Arial, sans-serif';

      contenedorTemporal.innerHTML = `
        <div style="padding: 25px; background: white; border: 3px solid #1f2937; border-radius: 12px; box-shadow: 0 4px 8px rgba(0,0,0,0.15);">
          <div style="text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #e5e7eb;">
            <h1 style="color: #1f2937; margin-bottom: 8px; font-size: 24px; font-weight: bold;">${nombreEmpresa}</h1>
            <p style="color: #6b7280; font-size: 14px; margin: 5px 0;">RUC: ${rucEmpresa}</p>
            <h2 style="color: #b91c1c; font-size: 18px; margin-top: 10px; font-weight: bold;">LIQUIDACIÓN DE TRABAJADOR</h2>
          </div>
          <div style="margin-bottom: 20px;">
            <h3 style="color: #374151; font-size: 14px; font-weight: bold; margin-bottom: 10px;">DATOS DEL TRABAJADOR</h3>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Nombre:</strong> ${liquidacion.empleado || ''}</p>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Cargo:</strong> ${liquidacion.cargo || '—'}</p>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Fecha ingreso:</strong> ${formatDatePdf(liquidacion.fecha_ingreso)}</p>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Fecha cese:</strong> ${formatDatePdf(liquidacion.fecha_cese)}</p>
          </div>
          ${bloqueConceptos}
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 25px;">
            <div style="text-align: center; background: #f9fafb; padding: 20px; border-radius: 10px; border: 2px solid #e5e7eb;">
              <div style="height: 60px; margin-bottom: 15px; display: flex; align-items: center; justify-content: center;">
                ${firmaEmpresaBase64
                  ? `<img src="${firmaEmpresaBase64}" style="width: 180px; height: auto; object-fit: contain;" alt="Firma del Empleador" />`
                  : '<p style="font-size: 10px; color: #6b7280;">Espacio para firma del empleador</p>'}
              </div>
              <p style="margin: 0; font-size: 12px; font-weight: bold; color: #374151;">FIRMA DEL EMPLEADOR</p>
            </div>
            <div style="text-align: center; background: #f9fafb; padding: 20px; border-radius: 10px; border: 2px solid #e5e7eb;">
              <div style="height: 60px; margin-bottom: 15px; border-bottom: 2px solid #374151;"></div>
              <p style="margin: 0; font-size: 12px; font-weight: bold; color: #374151;">FIRMA DEL TRABAJADOR</p>
              <p style="margin: 5px 0; font-size: 11px; color: #6b7280;">${liquidacion.empleado || ''}</p>
            </div>
          </div>
          <div style="text-align: center; color: #6b7280; font-size: 10px; padding-top: 15px; margin-top: 20px; border-top: 1px solid #e5e7eb;">
            <p style="margin: 3px 0;">Fecha de generación: ${new Date().toLocaleDateString('es-PE')}</p>
            <p style="margin: 3px 0;">Sistema de Planillas - ${nombreEmpresa}</p>
          </div>
        </div>
      `;

      document.body.appendChild(contenedorTemporal);
      const canvas = await html2canvas(contenedorTemporal, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });
      document.body.removeChild(contenedorTemporal);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const maxHeight = 297;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;
      if (imgHeight > maxHeight) imgHeight = maxHeight;
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

      const fechaActual = new Date().toISOString().split('T')[0];
      const nombreArchivo = `Liquidacion_${(liquidacion.empleado || 'empleado').replace(/\s+/g, '_')}_${fechaActual}.pdf`;
      pdf.save(nombreArchivo);
    } catch (err) {
      console.error('Error generando PDF liquidación:', err);
      setError(err?.message || 'Error al generar el PDF');
    } finally {
      setGenerandoPdfLiquidacion(false);
    }
  };

  useEffect(() => {
    loadLiquidaciones();
  }, [empresaId]);

  useEffect(() => {
    if (!detalleSeleccionado?.conceptos_ingresos?.length) {
      setConceptosIncluidos(new Set());
      return;
    }
    const otrosIndices = detalleSeleccionado.conceptos_ingresos
      .map((c, i) => (esConceptoBasico(c.descripcion) ? -1 : i))
      .filter((i) => i >= 0);
    setConceptosIncluidos(new Set(otrosIndices));
  }, [empleadoSeleccionado?.id]);

  useEffect(() => {
    setMontoVacacionesTruncasEditable(null);
    setVacacionesTruncasExpandido(false);
    setGratificacionExpandido(false);
    setMontoGratificacionEditable(null);
    setCtsExpandido(false);
    setMontoCtsEditable(null);
    setGratificacionEstados({});
    setGratificacionMontoPorPeriodo({});
    setGratificacionMontoMostrarInput({});
    setGratificacionMontoCargando({});
    setCtsEstados({});
    setCtsMontoPorPeriodo({});
    setCtsMontoMostrarInput({});
    setCtsMontoCargando({});
    setGratificacionParaCts(null);
    setGratificacionParaCtsMostrarInput(false);
    setGratificacionParaCtsCargando(false);
    setGratificacionParaCtsEditable(null);
    setBonificacionGraciosa(0);
    setIncluirEnPlanilla(false);
  }, [empleadoSeleccionado?.id]);

  // Resolver monto de gratificación por período: planilla cerrada (códigos 0406, 0313) → detalle gratificación → sino input manual
  useEffect(() => {
    if (!empresaId || !empleadoSeleccionado?.id) return;
    const detalle = calculoCompleto.find((d) => d.id === empleadoSeleccionado.id);
    if (!detalle) return;
    const remun = getRemuneracionBasica(detalle.conceptos_ingresos, detalle.remuneracion_referencia, conceptosIncluidos);
    const periodos = obtenerPeriodosGratificacion(
      remun,
      detalle.fecha_ingreso,
      detalle.fecha_cese,
      detalle.regimen_codigo,
      detalle.regimen
    );
    if (periodos.length === 0) return;

    const empleadoId = detalle.id;

    const periodLabelToNombrePlanilla = (label) => {
      const matchJulio = label.match(/^Julio (\d{4})$/);
      const matchDic = label.match(/^Diciembre (\d{4})$/);
      if (matchJulio) return `${matchJulio[1]}-07`;
      if (matchDic) return `${matchDic[1]}-12`;
      return null;
    };

    const LOG = (msg, data) => {
      console.log('[Liquidación Gratificación]', msg, data !== undefined ? data : '');
    };

    let cancelled = false;
    periodos.forEach(async (p) => {
      setGratificacionMontoCargando((prev) => ({ ...prev, [p.id]: true }));
      let montoResuelto = null;
      let mostrarInput = false;

      LOG(`--- Período: ${p.label} (empleadoId: ${empleadoId}), monto calculado: ${p.monto}`);

      try {
        const nombrePlanilla = periodLabelToNombrePlanilla(p.label);
        LOG(`nombrePlanilla buscado:`, nombrePlanilla);

        if (nombrePlanilla) {
          const [anioPlanilla, mesPlanilla] = nombrePlanilla.split('-').map(Number);
          LOG(`pidiendo info por periodo: ${p.label} → empresa=${empresaId}, anio=${anioPlanilla}, mes=${mesPlanilla} (la fecha da el ID de la planilla)`);

          try {
            const empleadosRes = await planillaCerradaService.obtenerEmpleadosCerradosPorPeriodo(empresaId, anioPlanilla, mesPlanilla);
            const periodoBackend = empleadosRes?.planilla_periodo;
            const planillaId = empleadosRes?.planilla_id;
            LOG(`planilla del periodo ${p.label}:`, { planilla_id: planillaId, planilla_nombre: empleadosRes?.planilla_nombre, planilla_periodo: periodoBackend });

            const empleados = empleadosRes?.data ?? empleadosRes ?? [];
            const empleadosArr = Array.isArray(empleados) ? empleados : [];
            LOG(`empleados cerrados en planilla ${p.label}:`, { total: empleadosArr.length, ids: empleadosArr.map((e) => e.empleado_id ?? e.id) });

            const emp = empleadosArr.find((e) => Number(e.empleado_id || e.id) === Number(empleadoId)) || null;
            LOG(`trabajador (empleadoId ${empleadoId}) está en planilla ${p.label}?:`, emp ? 'SÍ' : 'NO');

            if (emp?.boleta_calculada?.data) {
              const ingresos = emp.boleta_calculada.data.dataIngresos || [];
              const codigosGrat = ['0406', '0313'];
              const normalizarCodigo = (cod) => String(cod ?? '').trim().replace(/^0+/, '').padStart(4, '0');
              const conceptosGrat = ingresos.filter((c) => codigosGrat.includes(normalizarCodigo(c.codigo ?? c.codigo_plame)));
              const suma = conceptosGrat.reduce((s, c) => s + (Number(c.valor) ?? Number(c.monto) ?? 0), 0);
              LOG(`conceptos 0406/0313 en esta planilla:`, { totalIngresos: ingresos.length, conceptosGrat: conceptosGrat.map((c) => ({ codigo: c.codigo ?? c.codigo_plame, valor: c.valor ?? c.monto })), suma });
              if (suma >= 0) montoResuelto = Math.round(suma * 100) / 100;
              LOG(`monto desde planilla cerrada (${p.label}):`, montoResuelto);
            } else {
              LOG(`trabajador sin boleta_calculada o dataIngresos en esta planilla`);
            }
          } catch (err) {
            if (err?.response?.status === 404) {
              LOG(`no existe planilla cerrada para ${p.label} (${anioPlanilla}-${String(mesPlanilla).padStart(2, '0')})`);
            } else if (err?.response?.status === 400) {
              LOG(`planilla del periodo ${p.label} no está cerrada`);
            } else {
              LOG(`error al obtener por periodo:`, err?.message || err);
            }
          }
        }

        if (montoResuelto == null) {
          LOG(`buscando en detalle gratificación...`);
          const anio = p.label.match(/\d{4}/)?.[0] ? parseInt(p.label.match(/\d{4}/)[0], 10) : new Date().getFullYear();
          const esJulio = /^Julio /.test(p.label);
          const periodo = esJulio ? 'junio' : 'diciembre';
          const listRes = await gratificacionService.list(empresaId, anio);
          const lista = Array.isArray(listRes) ? listRes : listRes?.data ?? [];
          LOG(`lista gratificaciones (año ${anio}):`, { total: lista.length, items: lista.map((g) => ({ id: g.id, periodo: g.periodo ?? g.periodo_nombre, anio: g.anio ?? g.año })) });

          const grat = lista.find((g) => {
            const gAnio = g.anio ?? g.año ?? g.anio;
            const gPeriodo = (g.periodo || g.periodo_nombre || '').toLowerCase();
            return Number(gAnio) === anio && (gPeriodo === (esJulio ? 'junio' : 'diciembre') || (esJulio && gPeriodo.includes('junio')) || (!esJulio && gPeriodo.includes('diciembre')));
          });
          if (grat?.id) {
            const detalleGrat = await gratificacionService.getById(grat.id);
            const detalleList = detalleGrat?.detalle ?? detalleGrat?.data?.detalle ?? [];
            const fila = detalleList.find((d) => Number(d.empleado_id || d.id_empleado || d.empleado?.id) === Number(empleadoId));
            LOG(`detalle gratificación:`, { gratId: grat.id, detalleCount: detalleList.length, filaEmpleado: fila ? { gratificacionBruta: fila.gratificacionBruta, gratificacionNeta: fila.gratificacionNeta } : 'NO' });
            if (fila && (fila.gratificacionBruta != null || fila.gratificacionNeta != null)) {
              montoResuelto = Math.round(Number(fila.gratificacionBruta ?? fila.gratificacionNeta ?? 0) * 100) / 100;
              LOG(`monto desde detalle gratificación:`, montoResuelto);
            }
          }
          if (montoResuelto == null) mostrarInput = true;
        }

        LOG(`RESULTADO ${p.label}:`, { montoResuelto, mostrarInput, montoCalculado: p.monto, seUsara: montoResuelto ?? (mostrarInput ? p.monto : undefined) });
      } catch (err) {
        console.warn('[Liquidación Gratificación] error:', p.label, err);
        mostrarInput = true;
      }

      if (cancelled) return;
      setGratificacionMontoCargando((prev) => ({ ...prev, [p.id]: false }));
      setGratificacionMontoPorPeriodo((prev) => ({
        ...prev,
        [p.id]: montoResuelto != null ? montoResuelto : (mostrarInput ? p.monto : undefined),
      }));
      setGratificacionMontoMostrarInput((prev) => ({ ...prev, [p.id]: mostrarInput }));
    });

    return () => { cancelled = true; };
  }, [empresaId, empleadoSeleccionado?.id, conceptosIncluidos]);

  // Resolver monto de CTS por período: planilla cerrada (códigos 0904, 2010) → detalle CTS → sino input manual
  useEffect(() => {
    if (!empresaId || !empleadoSeleccionado?.id) return;
    const detalle = calculoCompleto.find((d) => d.id === empleadoSeleccionado.id);
    if (!detalle) return;
    const periodos = obtenerPeriodosCTS(detalle.fecha_ingreso, detalle.fecha_cese);
    if (periodos.length === 0) return;

    const empleadoId = detalle.id;

    const periodLabelToAnioMes = (label) => {
      const matchMayo = label.match(/^Mayo (\d{4})$/);
      const matchNov = label.match(/^Noviembre (\d{4})$/);
      if (matchMayo) return { anio: parseInt(matchMayo[1], 10), mes: 5 };
      if (matchNov) return { anio: parseInt(matchNov[1], 10), mes: 11 };
      return null;
    };

    const LOG = (msg, data) => {
      console.log('[Liquidación CTS]', msg, data !== undefined ? data : '');
    };

    let cancelled = false;
    periodos.forEach(async (p) => {
      setCtsMontoCargando((prev) => ({ ...prev, [p.id]: true }));
      let montoResuelto = null;
      let mostrarInput = false;

      try {
        const { anio: anioPlanilla, mes: mesPlanilla } = periodLabelToAnioMes(p.label) || {};
        if (anioPlanilla != null && mesPlanilla != null) {
          try {
            const empleadosRes = await planillaCerradaService.obtenerEmpleadosCerradosPorPeriodo(empresaId, anioPlanilla, mesPlanilla);
            const empleados = empleadosRes?.data ?? empleadosRes ?? [];
            const empleadosArr = Array.isArray(empleados) ? empleados : [];
            const emp = empleadosArr.find((e) => Number(e.empleado_id || e.id) === Number(empleadoId)) || null;

            if (emp?.boleta_calculada?.data) {
              const ingresos = emp.boleta_calculada.data.dataIngresos || [];
              const codigosCts = ['0904', '2010'];
              const normalizarCodigo = (cod) => String(cod ?? '').trim().replace(/^0+/, '').padStart(4, '0');
              const conceptosCts = ingresos.filter((c) => codigosCts.includes(normalizarCodigo(c.codigo ?? c.codigo_plame)));
              const suma = conceptosCts.reduce((s, c) => s + (Number(c.valor) ?? Number(c.monto) ?? 0), 0);
              if (suma >= 0) montoResuelto = Math.round(suma * 100) / 100;
              LOG(`monto desde planilla cerrada (${p.label}):`, montoResuelto);
            }
          } catch (err) {
            if (err?.response?.status === 404) LOG(`no existe planilla cerrada para ${p.label}`);
            else LOG(`error planilla ${p.label}:`, err?.message || err);
          }
        }

        if (montoResuelto == null) {
          LOG(`buscando en detalle CTS...`);
          try {
            const anio = p.label.match(/\d{4}/)?.[0] ? parseInt(p.label.match(/\d{4}/)[0], 10) : new Date().getFullYear();
            const esMayo = /^Mayo /.test(p.label);
            const periodoNombre = esMayo ? 'mayo' : 'noviembre';
            const listRes = await ctsService.list(empresaId, anio);
            const lista = Array.isArray(listRes) ? listRes : listRes?.data ?? [];
            const cts = lista.find((c) => {
              const pNom = (c.periodo || c.periodo_nombre || '').toLowerCase();
              return (c.anio ?? c.año) === anio && (pNom === periodoNombre || pNom.includes(periodoNombre));
            });
            if (cts?.id) {
              const detalleCts = await ctsService.getById(cts.id);
              const detalleList = detalleCts?.detalle ?? detalleCts?.data?.detalle ?? [];
              const fila = detalleList.find((d) => Number(d.empleado_id || d.id_empleado || d.empleado?.id) === Number(empleadoId));
              if (fila && (fila.monto != null || fila.cts != null || fila.monto_cts != null)) {
                montoResuelto = Math.round(Number(fila.monto ?? fila.cts ?? fila.monto_cts ?? 0) * 100) / 100;
                LOG(`monto desde detalle CTS:`, montoResuelto);
              }
            }
          } catch (err) {
            if (err?.response?.status !== 404) console.warn('[Liquidación CTS] detalle:', err?.message || err);
          }
          if (montoResuelto == null) mostrarInput = true;
        }

        LOG(`RESULTADO CTS ${p.label}:`, { montoResuelto, mostrarInput });
      } catch (err) {
        console.warn('[Liquidación CTS] error:', p.label, err);
        mostrarInput = true;
      }

      if (cancelled) return;
      setCtsMontoCargando((prev) => ({ ...prev, [p.id]: false }));
      setCtsMontoPorPeriodo((prev) => ({
        ...prev,
        [p.id]: montoResuelto != null ? montoResuelto : (mostrarInput ? 0 : undefined),
      }));
      setCtsMontoMostrarInput((prev) => ({ ...prev, [p.id]: mostrarInput }));
    });

    return () => { cancelled = true; };
  }, [empresaId, empleadoSeleccionado?.id, calculoCompleto]);

  // Resolver gratificación pagada para fórmula CTS: último periodo (diciembre preferido) → planilla cerrada (0406, 0313) → detalle gratificación → input 0
  useEffect(() => {
    if (!empresaId || !empleadoSeleccionado?.id) return;
    const detalle = calculoCompleto.find((d) => d.id === empleadoSeleccionado.id);
    if (!detalle) return;
    const remun = getRemuneracionBasica(detalle.conceptos_ingresos, detalle.remuneracion_referencia, conceptosIncluidos);
    const periodos = obtenerPeriodosGratificacion(
      remun,
      detalle.fecha_ingreso,
      detalle.fecha_cese,
      detalle.regimen_codigo,
      detalle.regimen
    );
    const empleadoId = detalle.id;

    const periodLabelToAnioMes = (label) => {
      const matchJulio = label.match(/^Julio (\d{4})$/);
      const matchDic = label.match(/^Diciembre (\d{4})$/);
      if (matchJulio) return { anio: parseInt(matchJulio[1], 10), mes: 7 };
      if (matchDic) return { anio: parseInt(matchDic[1], 10), mes: 12 };
      return null;
    };

    let cancelled = false;
    setGratificacionParaCtsCargando(true);

    (async () => {
      let montoResuelto = null;
      let mostrarInput = false;

      if (periodos.length === 0) {
        mostrarInput = true;
        montoResuelto = 0;
      } else {
        const ultimo = periodos[periodos.length - 1];
        const { anio: anioPlanilla, mes: mesPlanilla } = periodLabelToAnioMes(ultimo.label) || {};

        if (anioPlanilla != null && mesPlanilla != null) {
          try {
            const empleadosRes = await planillaCerradaService.obtenerEmpleadosCerradosPorPeriodo(empresaId, anioPlanilla, mesPlanilla);
            const empleados = empleadosRes?.data ?? empleadosRes ?? [];
            const empleadosArr = Array.isArray(empleados) ? empleados : [];
            const emp = empleadosArr.find((e) => Number(e.empleado_id || e.id) === Number(empleadoId)) || null;
            if (emp?.boleta_calculada?.data) {
              const ingresos = emp.boleta_calculada.data.dataIngresos || [];
              const codigosGrat = ['0406', '0313'];
              const normalizarCodigo = (cod) => String(cod ?? '').trim().replace(/^0+/, '').padStart(4, '0');
              const conceptosGrat = ingresos.filter((c) => codigosGrat.includes(normalizarCodigo(c.codigo ?? c.codigo_plame)));
              const suma = conceptosGrat.reduce((s, c) => s + (Number(c.valor) ?? Number(c.monto) ?? 0), 0);
              if (suma >= 0) montoResuelto = Math.round(suma * 100) / 100;
            }
          } catch (err) {
            if (err?.response?.status !== 404) console.warn('[Liquidación] gratificación para CTS planilla:', err?.message);
          }
        }

        if (montoResuelto == null) {
          try {
            const anio = ultimo.label.match(/\d{4}/)?.[0] ? parseInt(ultimo.label.match(/\d{4}/)[0], 10) : new Date().getFullYear();
            const esJulio = /^Julio /.test(ultimo.label);
            const periodoNombre = esJulio ? 'junio' : 'diciembre';
            const listRes = await gratificacionService.list(empresaId, anio);
            const lista = Array.isArray(listRes) ? listRes : listRes?.data ?? [];
            const grat = lista.find((g) => {
              const gPeriodo = (g.periodo || g.periodo_nombre || '').toLowerCase();
              return (g.anio ?? g.año) === anio && (gPeriodo === periodoNombre || gPeriodo.includes(periodoNombre));
            });
            if (grat?.id) {
              const detalleGrat = await gratificacionService.getById(grat.id);
              const detalleList = detalleGrat?.detalle ?? detalleGrat?.data?.detalle ?? [];
              const fila = detalleList.find((d) => Number(d.empleado_id || d.id_empleado || d.empleado?.id) === Number(empleadoId));
              if (fila && (fila.gratificacionBruta != null || fila.gratificacionNeta != null)) {
                montoResuelto = Math.round(Number(fila.gratificacionBruta ?? fila.gratificacionNeta ?? 0) * 100) / 100;
              }
            }
          } catch (err) {
            if (err?.response?.status !== 404) console.warn('[Liquidación] gratificación para CTS detalle:', err?.message);
          }
          if (montoResuelto == null) {
            mostrarInput = true;
            montoResuelto = 0;
          }
        }
      }

      if (cancelled) return;
      setGratificacionParaCtsCargando(false);
      setGratificacionParaCts(montoResuelto);
      setGratificacionParaCtsMostrarInput(mostrarInput);
    })();

    return () => { cancelled = true; };
  }, [empresaId, empleadoSeleccionado?.id, calculoCompleto, conceptosIncluidos]);

  const loadEmpleadosCesadosModal = (page = 1, search = '') => {
    if (!empresaId) return;
    setLoadingModal(true);
    liquidacionService
      .getEmpleadosCesados(empresaId, page, search)
      .then((res) => {
        setEmpleadosCesados(res.data || []);
        setPaginacion({
          total: res.total ?? 0,
          last_page: res.last_page ?? 1,
          current_page: res.current_page ?? 1,
        });
      })
      .catch(() => {
        setEmpleadosCesados([]);
        setPaginacion({ total: 0, last_page: 1, current_page: 1 });
      })
      .finally(() => setLoadingModal(false));
  };

  const handleAbrirNuevaLiquidacion = () => {
    setModalNuevaLiquidacion(true);
    setEmpleadoSeleccionado(null);
    setError(null);
    setSearchTerm('');
    setPaginaActual(1);
    skipAutoSearchRef.current = true;
    setLoadingModal(true);
    Promise.all([
      liquidacionService.getEmpleadosCesados(empresaId, 1, ''),
      liquidacionService.calcular(empresaId),
    ])
      .then(([resLista, resCalculo]) => {
        setEmpleadosCesados(resLista.data || []);
        setCalculoCompleto(resCalculo.data || []);
        setPaginacion({
          total: resLista.total ?? 0,
          last_page: resLista.last_page ?? 1,
          current_page: resLista.current_page ?? 1,
        });
      })
      .catch(() => {
        setEmpleadosCesados([]);
        setCalculoCompleto([]);
        setPaginacion({ total: 0, last_page: 1, current_page: 1 });
      })
      .finally(() => setLoadingModal(false));
  };

  // Búsqueda automática mientras escribe (debounce 350ms)
  useEffect(() => {
    if (!modalNuevaLiquidacion || empleadoSeleccionado || !empresaId) return;
    if (skipAutoSearchRef.current) {
      skipAutoSearchRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setPaginaActual(1);
      loadEmpleadosCesadosModal(1, searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm, modalNuevaLiquidacion, empleadoSeleccionado, empresaId]);

  const handlePagina = (nuevaPagina) => {
    if (nuevaPagina < 1 || nuevaPagina > paginacion.last_page) return;
    setPaginaActual(nuevaPagina);
    loadEmpleadosCesadosModal(nuevaPagina, searchTerm);
  };

  const handleSeleccionarEmpleado = (empleado) => {
    setEmpleadoSeleccionado(empleado);
  };

  const handleGenerarLiquidacion = () => {
    const detalle = calculoCompleto.find((d) => d.id === empleadoSeleccionado?.id);
    const calc = calculoLiquidacion;
    if (!detalle || !calc || !empresaId) return;
    const vacacionesMonto = montoVacacionesTruncasEditable != null ? Number(montoVacacionesTruncasEditable) : calc.vacaciones_truncas;
    const gratMonto = esRegimenMicroEmpresa ? 0 : (montoGratificacionEditable != null ? Number(montoGratificacionEditable) : gratificacionPendienteTotal);
    const ctsMonto = esRegimenMicroEmpresa ? 0 : (montoCtsEditable != null ? Number(montoCtsEditable) : ctsTotal);
    const bonifGraciosa = Number(bonificacionGraciosa) || 0;
    const totalLiquidacion = esRegimenMicroEmpresa
      ? Math.round((vacacionesMonto + bonifGraciosa) * 100) / 100
      : Math.round((vacacionesMonto + ctsMonto + gratMonto + bonifGraciosa) * 100) / 100;

    // Para PLAME: 0406 = solo base gratificación, 0313 = solo bonificación 9%. Bonificación graciosa sin código.
    let gratificacionBase = 0;
    let gratificacionBonificacion9 = 0;
    if (!esRegimenMicroEmpresa) {
      if (montoGratificacionEditable != null) {
        gratificacionBase = gratMonto;
        gratificacionBonificacion9 = 0;
      } else if (calc.gratificacion_periodos?.length === 0 && gratificacionSinPeriodosResult) {
        gratificacionBase = gratificacionSinPeriodosResult.montoBase ?? 0;
        gratificacionBonificacion9 = gratificacionSinPeriodosResult.bonificacion9 ?? 0;
      } else if (calc.gratificacion_periodos?.length > 0 && gratificacionCola?.monto != null) {
        gratificacionBase = gratificacionDePeriodosPendientes + (gratificacionCola.montoBase ?? 0);
        gratificacionBonificacion9 = gratificacionCola.bonificacion9 ?? 0;
      }
    }

    // Construir array de cálculos con todo el camino de las fórmulas para guardar
    const detalleCalculos = buildDetalleCalculos({
      calc,
      detalle,
      esRegimenMype,
      esRegimenMicroEmpresa,
      vacacionesMonto,
      gratMonto,
      ctsMonto,
      bonifGraciosa,
      totalLiquidacion,
      formatCurrency,
      gratificacionSinPeriodosResult: calculoLiquidacion?.gratificacion_periodos?.length === 0 ? gratificacionSinPeriodosResult : null,
      ctsSinPeriodosResult: calculoLiquidacion?.cts_periodos?.length === 0 ? ctsSinPeriodosResult : null,
      gratificacionCola: calculoLiquidacion?.gratificacion_periodos?.length > 0 ? gratificacionCola : 0,
      ctsCola: calculoLiquidacion?.cts_periodos?.length > 0 ? ctsCola : 0,
      gratificacionEstados,
      ctsEstados,
      gratificacionMontoPorPeriodo,
      ctsMontoPorPeriodo,
      gratificacionParaCtsValor,
      montoGratificacionPeriodo: (p) => (gratificacionMontoPorPeriodo[p.id] != null ? Number(gratificacionMontoPorPeriodo[p.id]) : p.monto),
      montoCtsPeriodo: (p) => (ctsMontoPorPeriodo[p.id] != null ? Number(ctsMontoPorPeriodo[p.id]) : 0),
      ultimoDiaMesUltimoPeriodo,
      primerDiaMesUltimoPeriodoCts,
      contarMesesCompletos,
      formatDateShort,
    });

    setGuardando(true);
    liquidacionService
      .store({
        empresa_id: empresaId,
        empleado_id: detalle.id,
        nombre_completo: detalle.empleado,
        cargo: detalle.cargo,
        fecha_ingreso: detalle.fecha_ingreso,
        fecha_cese: detalle.fecha_cese,
        motivo_cese: detalle.motivo_cese,
        remuneracion_referencia: calc.remuneracion_basica,
        gratificacion_proporcional: esRegimenMicroEmpresa ? 0 : gratMonto,
        gratificacion_base: gratificacionBase,
        gratificacion_bonificacion_9: gratificacionBonificacion9,
        cts: esRegimenMicroEmpresa ? 0 : ctsMonto,
        vacaciones_truncas: vacacionesMonto,
        total_liquidacion: totalLiquidacion,
        detalle_calculos: detalleCalculos,
        incluir_en_planilla: !!incluirEnPlanilla,
      })
      .then(() => {
        setModalNuevaLiquidacion(false);
        setEmpleadoSeleccionado(null);
        loadLiquidaciones();
        onLiquidacionesChange?.();
      })
      .catch((err) => setError(err.response?.data?.message || 'Error al guardar'))
      .finally(() => setGuardando(false));
  };

  const detalleSeleccionado = empleadoSeleccionado
    ? calculoCompleto.find((d) => d.id === empleadoSeleccionado.id)
    : null;

  const calculoLiquidacion = detalleSeleccionado
    ? (() => {
        const remun = getRemuneracionBasica(
          detalleSeleccionado.conceptos_ingresos,
          detalleSeleccionado.remuneracion_referencia,
          conceptosIncluidos
        );
        const vac = calcularVacacionesTruncas(
          remun,
          detalleSeleccionado.fecha_ingreso,
          detalleSeleccionado.fecha_cese,
          detalleSeleccionado.dias_vacaciones_gozadas ?? 0,
          detalleSeleccionado.dias_vacaciones_por_regimen ?? 30,
          detalleSeleccionado.dias_vacaciones_iniciales ?? 0
        );
        const cts = calcularCTS(remun, detalleSeleccionado.fecha_ingreso, detalleSeleccionado.fecha_cese);
        const ctsPeriodos = obtenerPeriodosCTS(detalleSeleccionado.fecha_ingreso, detalleSeleccionado.fecha_cese);
        const gratPeriodos = obtenerPeriodosGratificacion(
          remun,
          detalleSeleccionado.fecha_ingreso,
          detalleSeleccionado.fecha_cese,
          detalleSeleccionado.regimen_codigo,
          detalleSeleccionado.regimen
        );
        const grat = gratPeriodos.reduce((s, p) => s + p.monto, 0);
        return {
          remuneracion_basica: remun,
          vacaciones_truncas: vac.monto,
          vacaciones_formula: vac.formula,
          cts,
          cts_periodos: ctsPeriodos,
          gratificacion_proporcional: grat,
          gratificacion_periodos: gratPeriodos,
          total_liquidacion: Math.round((vac.monto + cts + grat) * 100) / 100,
        };
      })()
    : null;

  const esRegimenMype = detalleSeleccionado
    ? (() => {
        const codigo = (detalleSeleccionado.regimen_codigo || '').toUpperCase();
        const nombre = (detalleSeleccionado.regimen || '').toLowerCase();
        return ['MYPE', 'MYPP', 'MYPM'].includes(codigo) || nombre.includes('mype') || nombre.includes('micro') || nombre.includes('microempresa') || nombre.includes('pequeña') || nombre.includes('pequena');
      })()
    : false;
  // Solo micro empresa: se ocultan gratificación y CTS. Pequeña empresa: sí llevan gratificación y CTS (con 50% si aplica).
  const esRegimenMicroEmpresa = detalleSeleccionado
    ? (() => {
        const codigo = (detalleSeleccionado.regimen_codigo || '').toUpperCase();
        const nombre = (detalleSeleccionado.regimen || '').toLowerCase();
        return codigo === 'MYPM' || (nombre.includes('micro') && !nombre.includes('pequeña'));
      })()
    : false;

  // Por defecto todos los períodos de gratificación en "Pagado" para que el total responda en 0 si no hay cola
  useEffect(() => {
    const periodos = calculoLiquidacion?.gratificacion_periodos;
    if (!periodos?.length) return;
    setGratificacionEstados((prev) => {
      let changed = false;
      const next = { ...prev };
      periodos.forEach((p) => {
        if (next[p.id] === undefined) {
          next[p.id] = 'pagado';
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [empleadoSeleccionado?.id, calculoLiquidacion?.gratificacion_periodos?.length]);

  // Por defecto todos los períodos de CTS en "Pagado"
  useEffect(() => {
    const periodos = calculoLiquidacion?.cts_periodos;
    if (!periodos?.length) return;
    setCtsEstados((prev) => {
      let changed = false;
      const next = { ...prev };
      periodos.forEach((p) => {
        if (next[p.id] === undefined) {
          next[p.id] = 'pagado';
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [empleadoSeleccionado?.id, calculoLiquidacion?.cts_periodos?.length]);

  const montoGratificacionPeriodo = (p) =>
    gratificacionMontoPorPeriodo[p.id] != null ? Number(gratificacionMontoPorPeriodo[p.id]) : p.monto;
  const gratificacionDePeriodosPendientes = calculoLiquidacion?.gratificacion_periodos
    ? calculoLiquidacion.gratificacion_periodos.reduce(
        (sum, p) => sum + (gratificacionEstados[p.id] === 'pagado' ? 0 : montoGratificacionPeriodo(p)),
        0
      )
    : 0;
  const gratificacionCola = calculoLiquidacion?.gratificacion_periodos?.length && detalleSeleccionado
    ? calcularGratificacionCola(
        calculoLiquidacion.remuneracion_basica,
        detalleSeleccionado.fecha_cese,
        calculoLiquidacion.gratificacion_periodos,
        detalleSeleccionado.regimen_codigo,
        detalleSeleccionado.regimen
      )
    : 0;
  const gratificacionSinPeriodosResult = calculoLiquidacion?.gratificacion_periodos?.length === 0 && detalleSeleccionado
    ? calcularGratificacionIngresoACese(
        calculoLiquidacion.remuneracion_basica,
        detalleSeleccionado.fecha_ingreso,
        detalleSeleccionado.fecha_cese,
        detalleSeleccionado.regimen_codigo,
        detalleSeleccionado.regimen
      )
    : null;
  const gratificacionSinPeriodos = gratificacionSinPeriodosResult?.monto ?? 0;
  const todosPeriodosPagados = calculoLiquidacion?.gratificacion_periodos?.length
    ? calculoLiquidacion.gratificacion_periodos.every((p) => (gratificacionEstados[p.id] ?? 'pagado') === 'pagado')
    : true;
  const gratificacionPendienteTotal = Math.round(
    (gratificacionDePeriodosPendientes + (todosPeriodosPagados ? (gratificacionCola?.monto ?? 0) : 0) + gratificacionSinPeriodos) * 100
  ) / 100;

  const montoCtsPeriodo = (p) => (ctsMontoPorPeriodo[p.id] != null ? Number(ctsMontoPorPeriodo[p.id]) : 0);
  const ctsDePeriodosPendientes = calculoLiquidacion?.cts_periodos?.length
    ? calculoLiquidacion.cts_periodos.reduce(
        (sum, p) => sum + (ctsEstados[p.id] === 'pagado' ? 0 : montoCtsPeriodo(p)),
        0
      )
    : 0;
  const gratificacionParaCtsValor = gratificacionParaCtsEditable != null
    ? gratificacionParaCtsEditable
    : (gratificacionParaCts != null ? gratificacionParaCts : calculoLiquidacion?.remuneracion_basica ?? 0);

  // Cuando cambia la gratificación para CTS, el input del total CTS debe mostrar el resultado recalculado
  useEffect(() => {
    setMontoCtsEditable(null);
  }, [gratificacionParaCtsValor]);

  const ctsCola = calculoLiquidacion?.cts_periodos?.length && detalleSeleccionado
    ? calcularCtsCola(
        calculoLiquidacion.remuneracion_basica,
        detalleSeleccionado.fecha_cese,
        calculoLiquidacion.cts_periodos,
        gratificacionParaCtsValor
      )
    : 0;
  const todosCtsPeriodosPagados = calculoLiquidacion?.cts_periodos?.length
    ? calculoLiquidacion.cts_periodos.every((p) => (ctsEstados[p.id] ?? 'pagado') === 'pagado')
    : true;
  const ctsSinPeriodosResult = calculoLiquidacion?.cts_periodos?.length === 0 && detalleSeleccionado
    ? calcularCtsIngresoACese(
        calculoLiquidacion.remuneracion_basica,
        detalleSeleccionado.fecha_ingreso,
        detalleSeleccionado.fecha_cese,
        gratificacionParaCtsValor
      )
    : null;
  const ctsTotalRaw =
    calculoLiquidacion?.cts_periodos?.length > 0
      ? Math.round((ctsDePeriodosPendientes + (todosCtsPeriodosPagados ? ctsCola : 0)) * 100) / 100
      : (ctsSinPeriodosResult?.monto ?? calculoLiquidacion?.cts ?? 0);
  const ctsTotal = esRegimenMicroEmpresa ? 0 : (esRegimenMype ? Math.round(ctsTotalRaw * 0.5 * 100) / 100 : ctsTotalRaw);

  const esConceptoBasico = (desc) => {
    if (!desc || typeof desc !== 'string') return false;
    const d = desc.toUpperCase();
    return d.includes('REMUNERACIÓN') && d.includes('BÁSICO') || d.includes('JORNAL') && d.includes('BÁSICO');
  };

  const toggleConceptoIncluido = (idx) => {
    setConceptosIncluidos((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div className="space-y-4 px-1 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-6 gap-4">
        <div className="flex items-center space-x-4 min-w-0 flex-1">
          <button
            onClick={onVolver}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            ← Volver
          </button>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
              📋 Liquidaciones
            </h1>
            <p className="text-gray-600 text-sm">Liquidaciones generadas por empleados cesados</p>
          </div>
        </div>
        <button
          onClick={handleAbrirNuevaLiquidacion}
          className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-medium hover:from-red-600 hover:to-red-700 shrink-0"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nueva liquidación
        </button>
      </div>

      {/* Lista de liquidaciones generadas */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {error && (
          <div className="p-4 bg-red-50 border-b border-red-200 text-red-700 text-sm rounded-t-xl">
            {error}
          </div>
        )}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
            <span className="ml-3 text-gray-600">Cargando...</span>
          </div>
        ) : liquidaciones.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <span className="text-2xl">📋</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay liquidaciones generadas</h3>
            <p className="text-gray-500 text-sm mb-4">
              Cree una nueva liquidación seleccionando un empleado cesado.
            </p>
            <button
              onClick={handleAbrirNuevaLiquidacion}
              className="inline-flex items-center px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
            >
              Nueva liquidación
            </button>
          </div>
        ) : (
          <>
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empleado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha ingreso</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha cese</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Incl. planilla</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha generación</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {liquidaciones.map((l) => (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{l.empleado}</div>
                        {l.cargo && <div className="text-xs text-gray-500">{l.cargo}</div>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{formatDateToDDMMYYYY(l.fecha_ingreso)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{formatDateToDDMMYYYY(l.fecha_cese)}</td>
                      <td className="px-6 py-4 text-sm font-bold text-blue-600">{formatCurrency(l.total_liquidacion)}</td>
                      <td className="px-6 py-4 text-sm">
                        {l.incluir_en_planilla ? (
                          <span className="inline-flex items-center gap-1 text-green-600 font-medium" title="Incluido en planilla">Sí</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{l.fecha_generacion || '—'}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                          <button
                            type="button"
                            onClick={() => handleVerLiquidacion(l)}
                            className="inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Ver detalle de cálculos"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Ver
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBorrarLiquidacion(l)}
                            className="inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar liquidación"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Borrar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="lg:hidden divide-y divide-gray-200 p-4">
              {liquidaciones.map((l) => (
                <div key={l.id} className="py-4 flex flex-col gap-2">
                  <div className="font-medium text-gray-900">{l.empleado}</div>
                  <div className="text-xs text-gray-500">
                    {formatDateToDDMMYYYY(l.fecha_ingreso)} — {formatDateToDDMMYYYY(l.fecha_cese)}
                  </div>
                  <div className="font-bold text-blue-600">{formatCurrency(l.total_liquidacion)}</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleVerLiquidacion(l)}
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Ver
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBorrarLiquidacion(l)}
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Borrar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal Nueva liquidación */}
      {modalNuevaLiquidacion && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => !guardando && (setModalNuevaLiquidacion(false), setEmpleadoSeleccionado(null))}
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Nueva liquidación</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Seleccione un empleado cesado para ver el preview y generar la liquidación
                </p>
                {!empleadoSeleccionado && (
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      placeholder="Buscar por nombre o documento (se filtra al escribir)..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    />
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto min-h-0">
                {loadingModal ? (
                  <div className="flex flex-col justify-center items-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
                    <span className="mt-4 text-gray-600">Cargando empleados cesados...</span>
                  </div>
                ) : empleadoSeleccionado ? (
                  <div className="p-6">
                    {detalleSeleccionado ? (
                      <>
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-900">{detalleSeleccionado.empleado}</h4>
                          <p className="text-sm text-gray-500">
                            Ingreso: {formatDateToDDMMYYYY(detalleSeleccionado.fecha_ingreso)} — Cese:{' '}
                            {formatDateToDDMMYYYY(detalleSeleccionado.fecha_cese)}
                          </p>
                          {detalleSeleccionado.regimen && (
                            <p className="text-sm text-gray-500 mt-1">
                              Régimen: <span className="font-medium text-gray-700">{detalleSeleccionado.regimen}</span>
                            </p>
                          )}
                        </div>
                        {detalleSeleccionado.conceptos_ingresos?.length > 0 && (
                          <div className="mb-4 pb-4 border-b border-gray-200">
                            <h5 className="text-sm font-semibold text-gray-700 mb-2">Configuración del sueldo (ingresos)</h5>
                            <div className="space-y-1.5 text-sm">
                              {detalleSeleccionado.conceptos_ingresos.map((c, i) => {
                                const esBasico = esConceptoBasico(c.descripcion);
                                const tieneCheckbox = !esBasico;
                                const checked = esBasico || conceptosIncluidos.has(i);
                                return (
                                  <div key={i} className="flex items-center justify-between gap-2">
                                    <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                                      {tieneCheckbox ? (
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => toggleConceptoIncluido(i)}
                                          className="rounded border-gray-300 text-red-600 focus:ring-red-500 shrink-0"
                                        />
                                      ) : (
                                        <span className="w-4 shrink-0" aria-hidden />
                                      )}
                                      <span className={!tieneCheckbox ? 'text-gray-600' : checked ? 'text-gray-900' : 'text-gray-400 line-through'}>{c.descripcion}</span>
                                    </label>
                                    <span className={checked ? 'text-gray-900' : 'text-gray-400'}>{formatCurrency(c.monto)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="space-y-2 border-t pt-4">
                          <div>
                            <div
                              className="flex justify-between items-center text-sm gap-2"
                            >
                              <button
                                type="button"
                                onClick={() => setVacacionesTruncasExpandido((e) => !e)}
                                className="flex items-center gap-1.5 text-left text-gray-600 hover:text-gray-900 min-w-0"
                              >
                                <span className="text-gray-600">Vacaciones truncas</span>
                                <svg className={`w-4 h-4 shrink-0 transition-transform ${vacacionesTruncasExpandido ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              <span className="flex items-center gap-1 shrink-0">
                                <span className="text-green-600 text-sm">S/</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={montoVacacionesTruncasEditable != null ? montoVacacionesTruncasEditable : (calculoLiquidacion?.vacaciones_truncas ?? '')}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '') setMontoVacacionesTruncasEditable(null);
                                    else {
                                      const n = parseFloat(v);
                                      if (!Number.isNaN(n)) setMontoVacacionesTruncasEditable(n);
                                    }
                                  }}
                                  className="w-24 text-right rounded border border-gray-300 px-2 py-1 text-green-600 font-medium focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                />
                              </span>
                            </div>
                            {calculoLiquidacion?.vacaciones_formula && vacacionesTruncasExpandido && (
                              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                                <div className="font-medium text-gray-800 mb-2">Tiempo trabajado</div>
                                <table className="w-full text-left border border-gray-200 rounded overflow-hidden">
                                  <thead>
                                    <tr className="bg-gray-100 text-gray-700">
                                      <th className="px-3 py-2 font-semibold">Período</th>
                                      <th className="px-3 py-2 font-semibold">Días correspondientes</th>
                                      <th className="px-3 py-2 font-semibold">Descuento (gozados/iniciales)</th>
                                      <th className="px-3 py-2 font-semibold">Pendiente</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {calculoLiquidacion.vacaciones_formula.periodos_desglose?.map((p, idx) => (
                                      <tr key={idx} className="border-t border-gray-200">
                                        <td className="px-3 py-2">{p.nombre}</td>
                                        <td className="px-3 py-2">{p.dias_correspondientes} días</td>
                                        <td className="px-3 py-2 text-amber-700">{p.descontados > 0 ? `− ${p.descontados}` : '—'}</td>
                                        <td className="px-3 py-2 font-medium text-green-700">{p.pendiente} días</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                <div className="mt-2 space-y-0.5 text-xs text-green-700">
                                  <div>
                                    Días efectivamente gozados: <span className="font-medium">{calculoLiquidacion.vacaciones_formula.dias_gozados}</span>
                                    {calculoLiquidacion.vacaciones_formula.dias_iniciales > 0 && (
                                      <span> · Iniciales descontados: {calculoLiquidacion.vacaciones_formula.dias_iniciales}</span>
                                    )}
                                  </div>
                                  <div className="font-medium text-green-700 space-y-1">
                                    {(() => {
                                      const vf = calculoLiquidacion.vacaciones_formula;
                                      const D = vf.dias_por_ano_segun_regimen ?? 15;
                                      const parcial = vf.parcial && (vf.parcial.meses > 0 || vf.parcial.dias > 0);
                                      const anos = vf.periodos_completos ?? 0;
                                      if (parcial) {
                                        const { meses, dias } = vf.parcial;
                                        const parteMeses = Math.round((vf.remuneracion / 12) * (meses * D / 30) * 100) / 100;
                                        const parteDias = Math.round((vf.remuneracion / 360) * 15 * (dias / 30) * 100) / 100;
                                        const periodosCompletosPendientes = (vf.periodos_desglose || []).filter(
                                          (p) => /^Año\s+\d+/i.test(String(p.nombre || '')) && Number(p.pendiente || 0) > 0
                                        );
                                        const montoPeriodosCompletos = periodosCompletosPendientes.reduce(
                                          (sum, p) => sum + ((vf.remuneracion / 30) * Number(p.pendiente || 0)),
                                          0
                                        );
                                        return (
                                          <>
                                            {periodosCompletosPendientes.map((p, idx) => {
                                              const montoPeriodo = Math.round((vf.remuneracion / 30) * Number(p.pendiente || 0) * 100) / 100;
                                              return (
                                                <div key={`vac-periodo-${idx}`}>
                                                  Resultado {p.nombre}: ({formatCurrency(vf.remuneracion)}/30) × {p.pendiente} = {formatCurrency(montoPeriodo)}
                                                </div>
                                              );
                                            })}
                                            <div>Primer resultado (Remun/12): ({formatCurrency(vf.remuneracion)}/12) × ({meses} × {D}/30) = {formatCurrency(parteMeses)}</div>
                                            <div>Segundo resultado (Remun/360 × 15): ({formatCurrency(vf.remuneracion)}/360) × 15 × ({dias}/30) = {formatCurrency(parteDias)}</div>
                                            <div>Total: {formatCurrency(montoPeriodosCompletos)} + {formatCurrency(parteMeses)} + {formatCurrency(parteDias)} = {formatCurrency(vf.monto_dias_pendientes)}</div>
                                          </>
                                        );
                                      }
                                      if (anos > 0) {
                                        return <>({formatCurrency(vf.remuneracion)}/30) × ({anos} × {D}) = {formatCurrency(vf.monto_dias_pendientes)}</>;
                                      }
                                      return <>({formatCurrency(vf.remuneracion)}/30) × {vf.total_dias_pendientes} días = {formatCurrency(vf.monto_dias_pendientes)}</>;
                                    })()}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          {!esRegimenMicroEmpresa && (
                            <>
                              <div>
                                <div className="flex justify-between items-center text-sm gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setGratificacionExpandido((e) => !e)}
                                    className="flex items-center gap-1.5 text-left text-gray-600 hover:text-gray-900 min-w-0"
                                  >
                                    <span className="text-gray-600">Gratificación trunca</span>
                                    <svg className={`w-4 h-4 shrink-0 transition-transform ${gratificacionExpandido ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  <span className="flex items-center gap-1 shrink-0">
                                    <span className="text-green-600 text-sm">S/</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={montoGratificacionEditable != null ? montoGratificacionEditable : (gratificacionPendienteTotal ?? '')}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === '') setMontoGratificacionEditable(null);
                                        else {
                                          const n = parseFloat(v);
                                          if (!Number.isNaN(n)) setMontoGratificacionEditable(n);
                                        }
                                      }}
                                      className="w-24 text-right rounded border border-gray-300 px-2 py-1 text-green-600 font-medium focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    />
                                  </span>
                                </div>
                                {gratificacionExpandido && calculoLiquidacion?.gratificacion_periodos?.length > 0 ? (
                                  <>
                                    <table className="w-full text-sm border border-gray-200 rounded overflow-hidden">
                                      <thead>
                                        <tr className="bg-gray-100 text-gray-700">
                                          <th className="px-3 py-2 font-semibold text-left">Período</th>
                                          <th className="px-3 py-2 font-semibold text-right">Monto</th>
                                          <th className="px-3 py-2 font-semibold text-left">Estado</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {calculoLiquidacion.gratificacion_periodos.map((p) => {
                                          const estadoPeriodo = gratificacionEstados[p.id] ?? 'pagado';
                                          const esPendiente = estadoPeriodo === 'pendiente';
                                          return (
                                            <tr key={p.id} className="border-t border-gray-200">
                                              <td className="px-3 py-2">{p.label}</td>
                                              <td className="px-3 py-2 text-right">
                                                {esPendiente ? (
                                                  gratificacionMontoCargando[p.id] ? (
                                                    <span className="text-gray-400 text-sm">Cargando...</span>
                                                  ) : gratificacionMontoMostrarInput[p.id] ? (
                                                    <input
                                                      type="number"
                                                      step="0.01"
                                                      min="0"
                                                      value={gratificacionMontoPorPeriodo[p.id] ?? p.monto}
                                                      onChange={(e) => {
                                                        const v = parseFloat(e.target.value);
                                                        setGratificacionMontoPorPeriodo((prev) => ({ ...prev, [p.id]: isNaN(v) ? p.monto : v }));
                                                      }}
                                                      className="w-28 rounded border border-gray-300 px-2 py-1 text-sm text-right text-green-600 font-medium focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                                    />
                                                  ) : (
                                                    <span className="text-green-600 font-medium">{formatCurrency(montoGratificacionPeriodo(p))}</span>
                                                  )
                                                ) : (
                                                  <span className="text-gray-400">—</span>
                                                )}
                                              </td>
                                              <td className="px-3 py-2">
                                                <select
                                                  value={estadoPeriodo}
                                                  onChange={(e) => setGratificacionEstados((prev) => ({ ...prev, [p.id]: e.target.value }))}
                                                  className="rounded border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                                >
                                                  <option value="pagado">Pagado</option>
                                                  <option value="pendiente">Pendiente</option>
                                                </select>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                    <p className="text-xs text-gray-600 mt-1.5">
                                      Total gratificación (pendientes): <strong className="text-green-600">{formatCurrency(gratificacionPendienteTotal)}</strong>
                                    </p>
                                    {calculoLiquidacion.gratificacion_periodos.length > 0 && detalleSeleccionado && (gratificacionCola?.monto ?? 0) > 0 && (() => {
                                      const ref = ultimoDiaMesUltimoPeriodo(calculoLiquidacion.gratificacion_periodos);
                                      if (!ref) return null;
                                      const dCese = parseDateSafe(detalleSeleccionado.fecha_cese);
                                      const diaSiguiente = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() + 1);
                                      const { montoBase, bonificacion9, monto, meses, dias } = gratificacionCola;
                                      const remun = calculoLiquidacion.remuneracion_basica ?? 0;
                                      const refTexto = ref.getMonth() === 5 ? 'ene–jun. Cola desde 01/07' : 'jul–dic. Cola desde 01/01';
                                      return (
                                        <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded border border-gray-100">
                                          <div className="font-medium text-gray-700 mb-1">Cálculo gratificación desde último período (meses completos + días)</div>
                                          <div>Referencia: fin del semestre del último período = {formatDateShort(ref)} ({refTexto}).</div>
                                          <div>Desde (día siguiente) hasta cese: {formatDateShort(diaSiguiente)} → {formatDateShort(dCese)}</div>
                                          <div>Total en ese tramo: <strong>{meses} {meses === 1 ? 'mes' : 'meses'} y {dias} {dias === 1 ? 'día' : 'días'} restantes</strong></div>
                                          <div className="mt-1 text-green-700 font-medium space-y-0.5">
                                            <div>Base: (Remun/6) × (meses + días/30) {esRegimenMype ? '× 0,5 (MYPE)' : ''} = ({formatCurrency(remun)}/6) × ({meses} + {dias}/30) {esRegimenMype ? '× 0,5' : ''} = {formatCurrency(montoBase)}</div>
                                            <div>Bonificación extraord. 9% = (Remun × 9% / 6) × (meses + días/30) {esRegimenMype ? '× 0,5 (MYPE)' : ''} = ({formatCurrency(remun)} × 9% / 6) × ({meses} + {dias}/30) {esRegimenMype ? '× 0,5' : ''} = {formatCurrency(bonificacion9)}</div>
                                            <div>Total gratificación trunca = {formatCurrency(montoBase)} + {formatCurrency(bonificacion9)} = {formatCurrency(monto)}</div>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </>
                                ) : gratificacionExpandido ? (
                                  <>
                                    <div className="text-sm text-gray-500 mt-1.5">Sin periodos en el rango</div>
                                    {detalleSeleccionado && gratificacionSinPeriodosResult && (() => {
                                      const dIngreso = parseDateSafe(detalleSeleccionado.fecha_ingreso);
                                      const dCese = parseDateSafe(detalleSeleccionado.fecha_cese);
                                      const { totalDias, meses, dias, montoBase, bonificacion9 } = gratificacionSinPeriodosResult;
                                      const remun = calculoLiquidacion?.remuneracion_basica ?? 0;
                                      return (
                                        <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded border border-gray-100">
                                          <div className="font-medium text-gray-700 mb-1">Cálculo gratificación desde ingreso hasta cese</div>
                                          <div>Desde (fecha de ingreso) hasta cese: {formatDateShort(dIngreso)} → {formatDateShort(dCese)}</div>
                                          <div>Total en ese tramo: <strong>{meses} {meses === 1 ? 'mes' : 'meses'} y {dias} {dias === 1 ? 'día' : 'días'} restantes</strong></div>
                                          {totalDias > 0 && (
                                            <div className="mt-1 text-green-700 font-medium space-y-0.5">
                                              <div>Base: (Remun/6) × (meses + días/30) {esRegimenMype ? '× 0,5 (MYPE)' : ''} = ({formatCurrency(remun)}/6) × ({meses} + {dias}/30) {esRegimenMype ? '× 0,5' : ''} = {formatCurrency(montoBase)}</div>
                                              <div>Bonificación extraord. 9% = (Remun × 9% / 6) × (meses + días/30) {esRegimenMype ? '× 0,5 (MYPE)' : ''} = ({formatCurrency(remun)} × 9% / 6) × ({meses} + {dias}/30) {esRegimenMype ? '× 0,5' : ''} = {formatCurrency(bonificacion9)}</div>
                                              <div>Total gratificación = {formatCurrency(montoBase)} + {formatCurrency(bonificacion9)} = {formatCurrency(gratificacionSinPeriodos)}</div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </>
                                ) : null}
                              </div>
                              <div>
                                <div className="flex justify-between items-center text-sm gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setCtsExpandido((e) => !e)}
                                    className="flex items-center gap-1.5 text-left text-gray-600 hover:text-gray-900 min-w-0"
                                  >
                                    <span className="text-gray-600">CTS</span>
                                    <svg className={`w-4 h-4 shrink-0 transition-transform ${ctsExpandido ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  <span className="flex items-center gap-1 shrink-0">
                                    <span className="text-green-600 text-sm">S/</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={montoCtsEditable != null ? montoCtsEditable : (ctsTotal ?? '')}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === '') setMontoCtsEditable(null);
                                        else {
                                          const n = parseFloat(v);
                                          if (!Number.isNaN(n)) setMontoCtsEditable(n);
                                        }
                                      }}
                                      className="w-24 text-right rounded border border-gray-300 px-2 py-1 text-green-600 font-medium focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    />
                                  </span>
                                </div>
                                {ctsExpandido && (
                                  <>
                                    <p className="text-xs text-gray-500 mt-1.5">Compensación por Tiempo de Servicios. Períodos de depósito: mayo y noviembre.</p>
                                    {gratificacionParaCtsCargando && (
                                      <p className="text-xs text-gray-400 mt-1">Cargando gratificación pagada para fórmula CTS...</p>
                                    )}
                                    <div className="flex items-center gap-2 mt-1.5 text-sm">
                                      <span className="text-gray-600">Gratificación pagada (para fórmula CTS):</span>
                                      <span className="text-green-600">S/</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={gratificacionParaCtsEditable != null ? gratificacionParaCtsEditable : (gratificacionParaCts ?? 0)}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          if (v === '') setGratificacionParaCtsEditable(null);
                                          else {
                                            const n = parseFloat(v);
                                            if (!Number.isNaN(n)) setGratificacionParaCtsEditable(n);
                                          }
                                        }}
                                        className="w-24 text-right rounded border border-gray-300 px-2 py-1 text-green-600 font-medium focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                      />
                                    </div>
                                    {calculoLiquidacion?.cts_periodos?.length > 0 ? (
                                      <>
                                      <table className="w-full text-sm border border-gray-200 rounded overflow-hidden mt-2">
                                        <thead>
                                          <tr className="bg-gray-100 text-gray-700">
                                            <th className="px-3 py-2 font-semibold text-left">Período</th>
                                            <th className="px-3 py-2 font-semibold text-right">Monto</th>
                                            <th className="px-3 py-2 font-semibold text-left">Estado</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {calculoLiquidacion.cts_periodos.map((p) => {
                                            const estadoCts = ctsEstados[p.id] ?? 'pagado';
                                            const esPendiente = estadoCts === 'pendiente';
                                            const montoCtsPeriodo = ctsMontoPorPeriodo[p.id] != null ? Number(ctsMontoPorPeriodo[p.id]) : null;
                                            const mostrarInputCts = ctsMontoMostrarInput[p.id];
                                            const cargandoCts = ctsMontoCargando[p.id];
                                            return (
                                              <tr key={p.id} className="border-t border-gray-200">
                                                <td className="px-3 py-2">{p.label}</td>
                                                <td className="px-3 py-2 text-right">
                                                  {cargandoCts ? (
                                                    <span className="text-gray-400 text-sm">Cargando...</span>
                                                  ) : esPendiente && mostrarInputCts ? (
                                                    <input
                                                      type="number"
                                                      step="0.01"
                                                      min="0"
                                                      value={montoCtsPeriodo ?? ''}
                                                      onChange={(e) => {
                                                        const v = parseFloat(e.target.value);
                                                        setCtsMontoPorPeriodo((prev) => ({ ...prev, [p.id]: Number.isNaN(v) ? 0 : v }));
                                                      }}
                                                      className="w-28 rounded border border-gray-300 px-2 py-1 text-sm text-right text-green-600 font-medium focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                                    />
                                                  ) : esPendiente && montoCtsPeriodo != null ? (
                                                    <span className="text-green-600 font-medium">{formatCurrency(montoCtsPeriodo)}</span>
                                                  ) : (
                                                    <span className="text-gray-400">—</span>
                                                  )}
                                                </td>
                                                <td className="px-3 py-2">
                                                  <select
                                                    value={estadoCts}
                                                    onChange={(e) => setCtsEstados((prev) => ({ ...prev, [p.id]: e.target.value }))}
                                                    className="rounded border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                                  >
                                                    <option value="pagado">Pagado</option>
                                                    <option value="pendiente">Pendiente</option>
                                                  </select>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                      {calculoLiquidacion.cts_periodos.length > 0 && detalleSeleccionado && (() => {
                                        const desde = primerDiaMesUltimoPeriodoCts(calculoLiquidacion.cts_periodos);
                                        if (!desde) return null;
                                        const dCese = parseDateSafe(detalleSeleccionado.fecha_cese);
                                        const mesesCola = contarMesesCompletos(desde, dCese);
                                        const remun = calculoLiquidacion.remuneracion_basica ?? 0;
                                        const grat = gratificacionParaCtsValor;
                                        const remunComputable = remun + grat / 6;
                                        const mesesFormula = Math.min(6, mesesCola);
                                        const montoColaBruto = mesesCola < 1 ? 0 : Math.round((remunComputable / 12) * mesesFormula * 100) / 100;
                                        const montoCola = esRegimenMype ? Math.round(montoColaBruto * 0.5 * 100) / 100 : montoColaBruto;
                                        return (
                                          <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded border border-gray-100">
                                            <div className="font-medium text-gray-700 mb-1">Cálculo CTS desde último período</div>
                                            <div>En CTS el tiempo computable empieza el 1º del mes del depósito (Noviembre → 01/11, Mayo → 01/05).</div>
                                            <div>Desde (1º del mes) hasta cese: {formatDateShort(desde)} → {formatDateShort(dCese)}</div>
                                            <div>Meses completos en ese tramo: <strong>{mesesCola}</strong> {mesesCola < 1 ? '(menos de 1 mes = S/ 0)' : ''}</div>
                                            {mesesCola >= 1 && (() => {
                                              const unSextoGrat = Math.round((grat / 6) * 100) / 100;
                                              const ctsMensual = Math.round((remunComputable / 12) * 100) / 100;
                                              return (
                                                <div className="mt-1 text-green-700 font-medium space-y-0.5">
                                                  <div>Remun. computable = Remun + Grat./6 = {formatCurrency(remun)} + {formatCurrency(grat)}/6 = {formatCurrency(remun)} + {formatCurrency(unSextoGrat)} = {formatCurrency(remunComputable)}</div>
                                                  <div>CTS mensual = Remun. computable / 12 = {formatCurrency(remunComputable)} / 12 = {formatCurrency(ctsMensual)}</div>
                                                  <div>CTS = CTS mensual × meses {esRegimenMype ? '× 0,5 (MYPE)' : ''} = {formatCurrency(ctsMensual)} × {mesesFormula} {esRegimenMype ? '× 0,5' : ''} = {formatCurrency(montoCola)}</div>
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        );
                                      })()}
                                      </>
                                    ) : (
                                      <>
                                        <p className="text-xs text-gray-500 mt-2">Sin periodos en el rango (mayo/noviembre entre ingreso y cese).</p>
                                        {detalleSeleccionado && ctsSinPeriodosResult && (() => {
                                          const dIngreso = parseDateSafe(detalleSeleccionado.fecha_ingreso);
                                          const dCese = parseDateSafe(detalleSeleccionado.fecha_cese);
                                          const { totalDias, meses, dias, remunComputable } = ctsSinPeriodosResult;
                                          const remun = calculoLiquidacion?.remuneracion_basica ?? 0;
                                          const grat = gratificacionParaCtsValor;
                                          const unSextoGrat = Math.round((grat / 6) * 100) / 100;
                                          const ctsMensual = Math.round((remunComputable / 12) * 100) / 100;
                                          const ctsBruto = ctsSinPeriodosResult.monto;
                                          const ctsMontoMostrar = esRegimenMype ? Math.round(ctsBruto * 0.5 * 100) / 100 : ctsBruto;
                                          return (
                                            <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded border border-gray-100">
                                              <div className="font-medium text-gray-700 mb-1">Cálculo CTS desde ingreso hasta cese</div>
                                              <div>Desde (fecha de ingreso) hasta cese: {formatDateShort(dIngreso)} → {formatDateShort(dCese)}</div>
                                              <div>Total en ese tramo: <strong>{meses} {meses === 1 ? 'mes' : 'meses'} y {dias} {dias === 1 ? 'día' : 'días'} restantes</strong></div>
                                              {totalDias > 0 && (
                                                <div className="mt-1 text-green-700 font-medium space-y-0.5">
                                                  <div>Remun. computable = Remun + Grat./6 = {formatCurrency(remun)} + {formatCurrency(grat)}/6 = {formatCurrency(remun)} + {formatCurrency(unSextoGrat)} = {formatCurrency(remunComputable)}</div>
                                                  <div>CTS mensual = Remun. computable / 12 = {formatCurrency(remunComputable)} / 12 = {formatCurrency(ctsMensual)}</div>
                                                  <div>CTS = CTS mensual × (meses + días/30) {esRegimenMype ? '× 0,5 (MYPE)' : ''} = {formatCurrency(ctsMensual)} × ({meses} + {dias}/30) {esRegimenMype ? '× 0,5' : ''} = {formatCurrency(ctsMontoMostrar)}</div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </>
                          )}
                          <div className="flex justify-between items-center text-sm gap-2">
                            <span className="text-gray-600">Bonificación graciosa</span>
                            <span className="flex items-center gap-1 shrink-0">
                              <span className="text-green-600 text-sm">S/</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={bonificacionGraciosa ?? 0}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '') setBonificacionGraciosa(0);
                                  else {
                                    const n = parseFloat(v);
                                    if (!Number.isNaN(n) && n >= 0) setBonificacionGraciosa(n);
                                  }
                                }}
                                className="w-24 text-right rounded border border-gray-300 px-2 py-1 text-green-600 font-medium focus:ring-2 focus:ring-red-500 focus:border-red-500"
                              />
                            </span>
                          </div>
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mt-2">
                            <input
                              type="checkbox"
                              checked={!!incluirEnPlanilla}
                              onChange={(e) => setIncluirEnPlanilla(e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                            />
                            <span>Incluir en planilla</span>
                          </label>
                          <div className="flex justify-between font-bold text-lg border-t pt-4 mt-4">
                            <span>Total</span>
                            <span className="text-blue-600">
                              {formatCurrency(
                                esRegimenMicroEmpresa
                                  ? (montoVacacionesTruncasEditable != null ? montoVacacionesTruncasEditable : calculoLiquidacion?.vacaciones_truncas ?? 0) + (Number(bonificacionGraciosa) || 0)
                                  : (montoVacacionesTruncasEditable != null ? montoVacacionesTruncasEditable : calculoLiquidacion?.vacaciones_truncas ?? 0) +
                                    (montoCtsEditable != null ? montoCtsEditable : ctsTotal ?? 0) +
                                    (montoGratificacionEditable != null ? montoGratificacionEditable : gratificacionPendienteTotal) +
                                    (Number(bonificacionGraciosa) || 0)
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="mt-6 flex gap-2">
                          <button
                            onClick={() => setEmpleadoSeleccionado(null)}
                            disabled={guardando}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                          >
                            Cambiar empleado
                          </button>
                          <button
                            onClick={handleGenerarLiquidacion}
                            disabled={guardando}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                          >
                            {guardando ? 'Guardando...' : 'Generar liquidación'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-500">Calculando...</p>
                    )}
                  </div>
                ) : (
                  <>
                    <ul className="divide-y divide-gray-200">
                      {empleadosCesados.length === 0 ? (
                        <li className="px-6 py-8 text-center text-gray-500">No hay empleados cesados.</li>
                      ) : (
                        empleadosCesados.map((emp) => (
                          <li
                            key={emp.id}
                            onClick={() => handleSeleccionarEmpleado(emp)}
                            className="px-6 py-4 hover:bg-gray-50 cursor-pointer flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                          >
                            <div className="flex flex-col gap-0.5">
                              <span className="font-medium text-gray-900">{emp.empleado}</span>
                              <span className="text-sm text-gray-600">
                                Ingreso: {formatDateToDDMMYYYY(emp.fecha_ingreso)} — Cese: {formatDateToDDMMYYYY(emp.fecha_cese)}
                                {emp.regimen && (
                                  <span className="ml-2 text-gray-500">· Régimen: {emp.regimen}</span>
                                )}
                              </span>
                            </div>
                          </li>
                        ))
                      )}
                    </ul>
                    {paginacion.last_page > 1 && (
                      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          Página {paginacion.current_page} de {paginacion.last_page} ({paginacion.total} en total)
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handlePagina(paginacion.current_page - 1)}
                            disabled={paginacion.current_page <= 1 || loadingModal}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Anterior
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePagina(paginacion.current_page + 1)}
                            disabled={paginacion.current_page >= paginacion.last_page || loadingModal}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Siguiente
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="p-4 border-t bg-gray-50">
                <button
                  onClick={() => !guardando && (setModalNuevaLiquidacion(false), setEmpleadoSeleccionado(null))}
                  disabled={guardando}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal ver detalle de cálculos */}
      {liquidacionAVer && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="modal-ver-title">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setLiquidacionAVer(null)}
              aria-hidden="true"
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200 shrink-0">
                <h3 id="modal-ver-title" className="text-lg font-bold text-gray-900">
                  Detalle de cálculos — {liquidacionAVer.empleado}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Fórmulas y pasos guardados al generar la liquidación
                </p>
                {liquidacionAVer.incluir_en_planilla && (
                  <p className="text-sm font-medium text-green-700 mt-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                    Esta liquidación se incluirá en planilla
                  </p>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {!liquidacionAVer.detalle_calculos ? (
                  <p className="text-gray-500 text-sm">No hay detalle de cálculos guardado para esta liquidación.</p>
                ) : (
                  <>
                    {liquidacionAVer.detalle_calculos.vacaciones_truncas && (
                      <section className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                        <h4 className="font-semibold text-gray-900 mb-2">Vacaciones truncas — {formatCurrency(liquidacionAVer.detalle_calculos.vacaciones_truncas.monto)}</h4>
                        {esDetalleConsistente(liquidacionAVer.detalle_calculos.vacaciones_truncas) ? (
                          <ul className="text-sm text-green-700 space-y-1.5">
                            {liquidacionAVer.detalle_calculos.vacaciones_truncas.pasos?.map((p, i) => (
                              <li key={i} className="pl-2 border-l-2 border-green-300">
                                <span className="font-medium">{p.descripcion}</span>
                                {p.formula && <div className="mt-0.5">{p.formula}</div>}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </section>
                    )}
                    {liquidacionAVer.detalle_calculos.gratificacion_trunca &&
                      Number(liquidacionAVer.detalle_calculos.gratificacion_trunca.monto || 0) > 0 && (
                      <section className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                        <h4 className="font-semibold text-gray-900 mb-2">Gratificación trunca — {formatCurrency(liquidacionAVer.detalle_calculos.gratificacion_trunca.monto)}</h4>
                        {esDetalleConsistente(liquidacionAVer.detalle_calculos.gratificacion_trunca) ? (
                          <ul className="text-sm text-green-700 space-y-1.5">
                            {liquidacionAVer.detalle_calculos.gratificacion_trunca.pasos?.map((p, i) => (
                              <li key={i} className="pl-2 border-l-2 border-green-300">
                                <span className="font-medium">{p.descripcion}</span>
                                {p.formula && <div className="mt-0.5">{p.formula}</div>}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </section>
                    )}
                    {liquidacionAVer.detalle_calculos.cts && (
                      <section className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                        <h4 className="font-semibold text-gray-900 mb-2">CTS — {formatCurrency(liquidacionAVer.detalle_calculos.cts.monto)}</h4>
                        {esDetalleConsistente(liquidacionAVer.detalle_calculos.cts) ? (
                          <ul className="text-sm text-green-700 space-y-1.5">
                            {liquidacionAVer.detalle_calculos.cts.pasos?.map((p, i) => (
                              <li key={i} className="pl-2 border-l-2 border-green-300">
                                <span className="font-medium">{p.descripcion}</span>
                                {p.formula && <div className="mt-0.5">{p.formula}</div>}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </section>
                    )}
                    {liquidacionAVer.detalle_calculos.bonificacion_graciosa && (
                      <section className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                        <h4 className="font-semibold text-gray-900 mb-2">Bonificación graciosa — {formatCurrency(liquidacionAVer.detalle_calculos.bonificacion_graciosa.monto)}</h4>
                        {liquidacionAVer.detalle_calculos.bonificacion_graciosa.formula && (
                          <p className="text-sm text-green-700">{liquidacionAVer.detalle_calculos.bonificacion_graciosa.formula}</p>
                        )}
                      </section>
                    )}
                    {liquidacionAVer.detalle_calculos.total && (
                      <section className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/50">
                        <h4 className="font-semibold text-gray-900 mb-2">Total liquidación — {formatCurrency(liquidacionAVer.detalle_calculos.total.monto)}</h4>
                        <p className="text-sm text-green-700">{liquidacionAVer.detalle_calculos.total.formula}</p>
                      </section>
                    )}
                  </>
                )}
              </div>
              <div className="p-4 border-t bg-gray-50 shrink-0 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => exportarLiquidacionPdf(liquidacionAVer)}
                  disabled={generandoPdfLiquidacion}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generandoPdfLiquidacion ? 'Generando...' : 'Descargar constancia'}
                </button>
                <button
                  type="button"
                  onClick={() => setLiquidacionAVer(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminar liquidación */}
      {liquidacionABorrar && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="modal-borrar-title">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={handleCerrarModalBorrar}
              aria-hidden="true"
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 id="modal-borrar-title" className="text-lg font-bold text-gray-900">
                Eliminar liquidación
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                ¿Está seguro de eliminar la liquidación de <strong>{liquidacionABorrar.empleado}</strong>?
                Esta acción no se puede deshacer.
              </p>
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={handleCerrarModalBorrar}
                  disabled={borrando}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmarBorrar}
                  disabled={borrando}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {borrando ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Eliminando...
                    </>
                  ) : (
                    'Eliminar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiquidacionesDetalle;
